// GameBoard render tree, extracted from GameBoard.jsx. Every function
// takes the GameBoard instance as `host` and reads host.state /
// host.props / host.* handlers exactly as the original class methods
// did; GameBoard.render() wraps renderBody(this) in its perf marks.

import { ACCENT, ACCENT_BRIGHT, ACCENT_GOLD, ACCENT_LO, BEVELED_BTN, DIALOG_STYLE, FourCorners, GOLD, GOLD_BTN, INPUT_STYLE, PANEL_BG, PANEL_STYLE, POPOVER_STYLE, RADIUS, SECTION_HEADER_STYLE, TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { cn } from '../../lib/utils';
import { resolveAvatarUrl, resolveDeckAvatar } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import { sendDraftMatchAck } from '../../utils/arena/draftApi';
import { getMatchQuestMeta } from '../../utils/arena/questMeta';
import { saveLogbookTab } from '../../utils/arena/logbookSettings';
import { UI, playUI } from '../../utils/arena/uiSounds';
import { isDevBuild } from '../../utils/devMode';
import { resolveAvatarRules } from '../../utils/game/avatarRules';
import DiceTrayDrawer from './DiceTrayDrawer';
import OracleOverlay from '../play/OracleOverlay';
import OmenProgress from '../play/OmenProgress';
import BattlemageOverlay from '../play/BattlemageOverlay';
import FerocityProgress from '../play/FerocityProgress';
import HumourChip from '../laboratory/HumourChip';
import { isAlchemist } from '../../utils/arena/alchemist';
import { getSkillState } from '../../utils/arena/skills';
import * as oracleReading from './controllers/oracleReading';
import * as battlemagePledge from './controllers/battlemagePledge';
import { OP, isDragonIdentityActive, isFireSite, projectilePaths } from '../../utils/game/engine/automation';
import { matchesFilter } from '../../utils/game/engine/automation/cardFilter';
import { buildServerPickPrompt } from './controllers/authChoice';
import { isEligibleDiscardPick } from './controllers/automationRuntime';
import { lockedEquipmentIdSet } from './controllers/boardLegality';
import { canEndMatch } from './controllers/gameLifecycle';
import { isChatVisibleTab, logbookEntriesFor, logEntryText } from './controllers/logbookChat';
import { FitText } from '../ui/FitText';
import { getActiveEvent, getAffordability, getPileEvents } from '../../utils/game/engine/core';
import { describeBinding, getBinding } from '../../utils/game/keybindings';
import { COMPONENT_KEYS } from '../../utils/game/playmatComponentEditor';
import { disconnectSocket } from '../../utils/game/socketClient';
import { isAuthMatchEnabled } from '../../utils/game/net/authMatchFlag';
import { playSound } from '../../utils/game/sounds';
import { SPAWN_COLORS, SPAWN_LABELS, getGameGrid, isTrackerConfigured } from '../../utils/game/spawnConfig';
import { PLAYER_LABELS, TRACKER_DEFS, getTrackerProgressLabel, getTrackerSpawnEntries } from '../../utils/game/trackerConfig';
import { COMPONENT_PLACEMENTS } from '../../utils/game/uiOverlayGenerator';
import { isControllerEnabled } from '../../utils/input/controllerFeature';
import { getLocalApiOrigin, resolveLocalImageUrl } from '../../utils/localApi';
import { perf } from '../../utils/perfMonitor';
import { askConfirm } from '../../utils/confirmBus';
import { api } from '../../utils/serverClient';
import { settleWithin } from '../../utils/settleWithin';
import { FOIL_OVERLAY_CLASSES, isFoilFinish } from '../../utils/sorcery/foil.js';
import { foilZonesForCard } from '../../utils/sorcery/foilZones.js';
import FoilCanvas from '../FoilCanvas';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams.js';
import ArenaMatchResult from '../ArenaMatchResult';
import AudioMixerPopover from '../AudioMixerPopover';
import CardHoverPreview from '../CardHoverPreview';
import CardInspector from '../CardInspector';
import CardMentionEditor from '../CardMentionEditor';
import CardMentionText from '../CardMentionText';
import PlayerTitle from '../PlayerTitle';
import PlaymatPicker, { getCurrentPlaymatThemeId, getVanillaPlaymatUrl } from '../PlaymatPicker';
import RuneSpinner from '../RuneSpinner';
import TutorialOverlay from '../TutorialOverlay';
import AttackTargetModal from './AttackTargetModal';
import CardActionMenu from './CardActionMenu';
import CardAltZoomOverlay from './CardAltZoomOverlay';
import CardPreviewDrawer from './CardPreviewDrawer';
import CellElementPips from './CellElementPips';
import CellStackBadges from './CellStackBadges';
import CellFanChrome from './CellFanChrome';
import CellTargetOverlays from './CellTargetOverlays';
import CellMentionHighlights from './CellMentionHighlights';
import { ControllerAimReticle } from './ControllerAimReticle';
import CombatOverlay from './CombatOverlay';
import CombatProgressPanel from './CombatProgressPanel';
import HudLifeTicker from './HudLifeTicker';
import DeckSpawnDialog from './DeckSpawnDialog';
import EffectPromptDrawer, { serverChoiceDisplayCard } from './EffectPromptDrawer';
import EquipPendingOverlays from './EquipPendingOverlays';
import EquipTargetModal from './EquipTargetModal';
import EquipmentStacks from './EquipmentStacks';
import FloatingDamageNumber from './FloatingDamageNumber';
import GameCodexPanel from './GameCodexPanel';
import GameContextMenu from './GameContextMenu';
import HarbingerSetupOverlay from './HarbingerSetupOverlay';
import HoverEquippedCards from './HoverEquippedCards';
import HoverStatPills from './HoverStatPills';
import HoverStatusBadges from './HoverStatusBadges';
import { HudBuffPanel } from './HudBuffPanel';
import { HudLifeAdjustPanel } from './HudLifeAdjustPanel';
import { HudScrollAdjuster } from './HudScrollAdjuster';
import InvalidCellHoverOverlay from './InvalidCellHoverOverlay';
import MovementReachOverlay from './MovementReachOverlay';
import MulliganOverlay from './MulliganOverlay';
import PileFanMenu from './PileFanMenu';
import PileSearchDialog from './PileSearchDialog';
import { PingReticle } from './PingReticle';
import ProjectilePathOverlays from './ProjectilePathOverlays';
import { RadialActionMenu } from './RadialActionMenu';
import ScryDialog from './ScryDialog';
import StartDrawOverlay from './StartDrawOverlay';
import StatusRingMenu from './StatusRingMenu';
import StatusTokenDrawer from './StatusTokenDrawer';
import StorylinePile from './StorylinePile';
import SpellCastFlightLayer from './SpellCastFlightLayer';
import UnitHandBadges from './UnitHandBadges';
import TokenSpawnPreview from './TokenSpawnPreview';
import { BOARD_TUTORIAL_STEPS, ShortcutList, buildCellStackTutorialSteps, buildFirstUnitTutorialSteps } from './boardTutorialSteps';
import { CELL_STACK_TUTORIAL_KEY } from './controllers/eventTutorials';
import { projectMeshScreenRect } from './controllers/overlayProjections';
import * as harbingerCeremony from './controllers/harbingerCeremony';
import { renderLogTextWithCells, stripLogCellBrackets } from './logFormatting';
import { CaveIcon, SunIcon } from './regionViewIcons';
import FirstPlayerDiceModal from '../play/FirstPlayerDiceModal';
import { IconAtlas, IconSpellbook } from '../ui/icons';
import { CloseButton } from '../ui/navButtons';
import { Tooltip } from '../ui/tooltip';
import { t } from '../../localization/t';
import { Component, createRef } from 'preact';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

// Cap on best-effort save/cleanup before a leave-match action gives up
// and exits anyway. A healthy local save is sub-100ms; this only bites
// when a fetch is wedged (e.g. a half-open localhost socket on Windows),
// where the user must never be trapped in the match. See settleWithin.
const LEAVE_CLEANUP_TIMEOUT_MS = 4000;

/**
 * Write the current this.marquee state into the DOM overlay div
 * directly, bypassing React state. This runs on every mouse-move
 * while the marquee is active and state churn would be wasteful.
 * Called with null (or an absent marquee) to hide the overlay.
 */
function mirrorSeatAvatar(host, owner) {
  const cards = host._authMirror?.getState?.()?.cards;
  if (!cards || !Array.isArray(host.props.sorceryCards)) return null;
  for (const mc of Object.values(cards)) {
    if (mc?.owner !== owner || mc.cardId == null) continue;
    const cat = host.props.sorceryCards.find((c) => c.unique_id === mc.cardId);
    if (cat?.type === 'Avatar') return { name: cat.name || null, url: cat.printings?.[0]?.image_url || null };
  }
  return null;
}

// Both seats' display identity (name + avatar art). In sandbox the user
// pilots both seats, so each side is identified by the Avatar card of the
// deck spawned there rather than the user's profile.
export function resolveMatchup(host) {
  const isSandbox = !!host.props.isCasualSandbox;
  const profile = host.props.profile;
  const profileName = profile?.name || profile?.username || 'You';
  // Self-avatar: prefer the pre-resolved arena URL, fall back
  // to resolving the profile's avatar card id ourselves so
  // sandbox / non-arena matches still get an image.
  const selfAvatar = host.props.arenaPlayerInfo?.avatarUrl
    || resolveAvatarUrl(profile, host.props.sorceryCards);
  const opponentInfo = host.props.arenaOpponentInfo;
  // In sandbox the user pilots both seats, so identify each
  // side by the Avatar card of the deck spawned in that seat
  // ("Sparkmage" vs "Dragonlord") rather than the user's own
  // profile. Seat suffixes only return when both decks run
  // the same avatar (or a deck somehow lacks one). Without
  // them the two sides would be indistinguishable.
  // Solo arms the roll-off from the mirror Full BEFORE the asset-gated
  // deck spawn fills spawnedDecks, so fall back to the seat's Avatar card
  // in the mirror (server truth, present from the first fold).
  const seatAvatar = (seat) => resolveDeckAvatar(host.state.spawnedDecks?.[seat], host.props.sorceryCards)
    || mirrorSeatAvatar(host, `p${seat}`);
  const seat1Avatar = isSandbox ? seatAvatar(1) : null;
  const seat2Avatar = isSandbox ? seatAvatar(2) : null;
  const sameAvatarName = seat1Avatar?.name && seat1Avatar.name === seat2Avatar?.name;
  const seatName = (avatar, seat) => {
    if (!avatar?.name) return t('{name} (Seat {seat})', { name: profileName, seat });
    return sameAvatarName ? t('{name} (Seat {seat})', { name: avatar.name, seat }) : avatar.name;
  };
  const localName = isSandbox ? seatName(seat1Avatar, 1) : profileName;
  const remoteName = isSandbox
    ? seatName(seat2Avatar, 2)
    : (opponentInfo?.name || t('Opponent'));
  const localAvatarUrl = isSandbox
    ? (seat1Avatar?.url || selfAvatar)
    : selfAvatar;
  const remoteAvatarUrl = isSandbox
    ? (seat2Avatar?.url || selfAvatar)
    : (opponentInfo?.avatarUrl || resolveAvatarUrl(opponentInfo, host.props.sorceryCards));
  if (isDevBuild() && (!localAvatarUrl || !remoteAvatarUrl)) {
    console.warn('[resolveMatchup] missing avatar art', {
      isSandbox,
      seat1: seat1Avatar,
      seat2: seat2Avatar,
      selfAvatar,
      spawned: [!!host.state.spawnedDecks?.[1], !!host.state.spawnedDecks?.[2]],
      mirrorCards: Object.keys(host._authMirror?.getState?.()?.cards || {}).length,
    });
  }
  return { localName, remoteName, localAvatarUrl, remoteAvatarUrl };
}

// The Oracle's reading: the omen overlay while the server's setup prompt is
// open for this seat, then the kept omen's progress chip. The reading view
// is asked for once per board; the server says whether this room takes one.
function renderOracle(host) {
  if (host.state.oracleReading === undefined && !host._oracleLoading && (host.state.roomCode || host.props.joinRoomCode)) {
    // ponytail: kicked from render because no lifecycle hook sees the room code land; guarded to one call
    queueMicrotask(() => oracleReading.loadOracleReading(host));
  }
  const reading = host.state.oracleReading;
  if (host.state.oraclePrompt && reading !== undefined && (!reading || reading.pending?.omenId)) {
    queueMicrotask(() => oracleReading.answerPrompt(host, reading?.pending?.omenId ? 'sealed' : null));
    return null;
  }
  if (host.state.oraclePrompt && reading) {
    return (
      <OracleOverlay
        reading={reading}
        skillState={getSkillState()}
        busy={!!host.state.oracleBusy}
        error={host.state.oracleError || null}
        onReroll={() => oracleReading.rerollOmens(host)}
        onSeal={(omenId) => oracleReading.sealOmen(host, omenId)}
      />
    );
  }
  const pending = reading?.pending;
  const omen = pending?.omenId ? pending.omens?.find((o) => o.id === pending.omenId) : null;
  if (!omen || host.state.showFirstPlayerDice) return null;
  return <OmenProgress omen={omen} stats={host._authMirror?.getState?.()?.stats} seat={host._localSeat()} />;
}

// The Battlemage's standing: the pledge overlay while the server's setup
// prompt is open for this seat, then the Ferocity chip reading the mirror.
// Asked for once per board, like the Oracle's reading.
function renderBattlemage(host) {
  if (host.state.battlemageStanding === undefined && !host._battlemageLoading && (host.state.roomCode || host.props.joinRoomCode)) {
    queueMicrotask(() => battlemagePledge.loadStanding(host));
  }
  const standing = host.state.battlemageStanding;
  if (host.state.battlemagePrompt && standing !== undefined && (!standing || standing.pending?.guard != null)) {
    queueMicrotask(() => battlemagePledge.answerPrompt(host, standing?.pending?.guard != null ? 'sealed' : null));
    return null;
  }
  if (host.state.battlemagePrompt && standing) {
    return (
      <BattlemageOverlay
        standing={standing}
        busy={!!host.state.battlemageBusy}
        error={host.state.battlemageError || null}
        onSeal={(guard) => battlemagePledge.sealPledge(host, guard)}
      />
    );
  }
  if (!standing || host.state.showFirstPlayerDice) return null;
  return <FerocityProgress standing={standing} stats={host._authMirror?.getState?.()?.stats} seat={host._localSeat()} skillState={getSkillState()} />;
}

// The Alchemist's harvest chip: humours gathered this match from the
// mirror's kill counters, and the potions readied for it. A sandbox pays
// no humours, so it shows none.
function renderAlchemist(host) {
  if (host.props.isCasualSandbox || host.state.showFirstPlayerDice || !isAlchemist(getSkillState())) return null;
  const mirror = host._authMirror?.getState?.();
  if (!mirror) return null;
  return (
    <HumourChip
      stats={host.state.harvestStats || mirror.stats || null}
      seat={host._localSeat()}
      skills={getSkillState()}
      alchemist={host.props.profile?.alchemist}
      // Same stage-clear gate as the deferred game over: no combat scene up
      // and no combat record at all (the resolved record lingers until the
      // playback has closed and the casualties have flown).
      hold={Boolean(host.state.combatScene) || Boolean(host.state.gameState?.pendingCombat)}
    />
  );
}

export function renderMarqueeOverlay(host) {
  const el = host.marqueeOverlayRef.current;
  if (!el) return;
  if (!host.marquee) {
    el.style.display = 'none';
    return;
  }
  const minX = Math.min(host.marquee.originX, host.marquee.currentX);
  const maxX = Math.max(host.marquee.originX, host.marquee.currentX);
  const minY = Math.min(host.marquee.originY, host.marquee.currentY);
  const maxY = Math.max(host.marquee.originY, host.marquee.currentY);
  el.style.display = 'block';
  el.style.left = `${minX}px`;
  el.style.top = `${minY}px`;
  el.style.width = `${maxX - minX}px`;
  el.style.height = `${maxY - minY}px`;
}

export function renderCardInspector(host) {
  const { inspectedCard } = host.state;
  if (!inspectedCard) return null;

  const { sorceryCards } = host.props;
  let fullCard = sorceryCards?.find((c) => c.unique_id === inspectedCard.cardId) || {};

  // Dragonlord merged state. While the borrow is active the
  // instance's cardId points at the companion Dragon, which would
  // make the main panel read as the Dragon. Re-anchor the main panel
  // to Dragonlord's printed card and surface the Dragon in a
  // dedicated Gained Abilities side panel instead. Before the
  // invoke, the owner sees the same panel in its muted Set Aside
  // state (the companion is private, so the opponent's client has
  // no _dragonlordCompanion and renders nothing).
  let gainedAbilities = null;
  if (isDragonIdentityActive(inspectedCard)) {
    const originalId = inspectedCard._dragonIdentitySnapshot?.cardId
      || inspectedCard._originalIdentity?.cardId;
    const dragonlordFull = sorceryCards?.find((c) => c.unique_id === originalId) || null;
    if (dragonlordFull && fullCard.unique_id && originalId !== inspectedCard.cardId) {
      gainedAbilities = {
        card: fullCard,
        imageUrl: resolveLocalImageUrl(fullCard.printings?.[0]?.image_url || null),
        active: true,
      };
      fullCard = dragonlordFull;
    }
  } else if (inspectedCard._dragonlordCompanion) {
    const dragonFull = sorceryCards?.find((c) => c.unique_id === inspectedCard._dragonlordCompanion) || null;
    if (dragonFull) {
      gainedAbilities = {
        card: dragonFull,
        imageUrl: resolveLocalImageUrl(dragonFull.printings?.[0]?.image_url || null),
        active: false,
      };
    }
  }

  // Imposter mask alternate view. When inspecting a masked imposter,
  // hand the inspector the imposter's catalog card as a side view so
  // a button at the bottom can flip the display between the wearer
  // (the masked avatar, default) and the imposter itself. Inspector-
  // only; never touches the on-table cardInstance.
  let alternateView = null;
  if (inspectedCard._isImposter && inspectedCard._originalIdentity) {
    const original = inspectedCard._originalIdentity;
    const imposterFull = sorceryCards?.find((c) => c.unique_id === original.cardId) || null;
    if (imposterFull) {
      alternateView = {
        card: imposterFull,
        imageUrl: resolveLocalImageUrl(original.imageUrl),
        foiling: original.foiling,
        rarity: imposterFull.rarity,
        label: imposterFull.name || 'Imposter',
      };
    }
  }

  const { inspectorStack } = host.state;
  const parentCard = inspectorStack.length > 0 ? inspectorStack[inspectorStack.length - 1] : null;

  return (
    <CardInspector
      card={fullCard}
      imageUrl={resolveLocalImageUrl(inspectedCard.imageUrl)}
      rarity={fullCard.rarity}
      foiling={inspectedCard.foiling}
      // Corruptor-style subtype rewrite lives on the in-play instance
      // (set by recomputeStaticAuras), not the catalog card. Hand it to
      // the inspector so the type line reflects the transformed identity.
      subtypeMap={inspectedCard._auraSubtypeMap || null}
      alternateView={alternateView}
      gainedAbilities={gainedAbilities}
      automationsActive={host._automationsEnabled?.() !== false}
      statuses={inspectedCard.statuses || []}
      equippedCards={inspectedCard.equippedCards || []}
      lockedEquipmentIds={lockedEquipmentIdSet(host)}
      sorceryCards={sorceryCards}
      parentCardName={parentCard?.name || null}
      onBack={parentCard ? () => host.popInspector() : null}
      onInspectEquipped={(eq) => host.pushInspector(eq)}
      onDetachEquipment={(eqId) => host.detachEquipment(inspectedCard, eqId)}
      // Seed the inspector with whichever face the on-table card is
      // currently showing. `onFlipChange` is intentionally omitted.
      // flipping inside the inspector previews the other side
      // without touching the actual table state, so a player
      // can't accidentally flip their Druid avatar just by looking
      // at it.
      initialFace={inspectedCard.face || 'front'}
      onClose={() => { host.setState({ inspectedCard: null, inspectorStack: [] }); host.scene?.setPaused(false); }}
    />
  );
}

export function renderLightingControls(host) {
  const current = host.scene?.getLightingMode?.() || 'dynamic';
  const modes = [
    { id: 'dynamic', label: t('Dynamic') },
    { id: 'day', label: t('Day') },
    { id: 'night', label: t('Night') },
  ];
  return (
    <div className="px-1">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t('Lighting')}</span>
      </div>
      <div className="flex items-center gap-1">
        {modes.map((mode) => {
          const active = current === mode.id;
          return (
            <button
              key={mode.id}
              type="button"
              className="flex-1 py-1 text-[0.625cqw] font-semibold text-center transition-all cursor-pointer"
              style={{
                borderRadius: 'var(--radius-sm)',
                background: active ? `${GOLD} 0.15)` : 'transparent',
                border: active ? `1px solid ${GOLD} 0.3)` : '1px solid transparent',
                color: active ? ACCENT_GOLD : TEXT_MUTED,
              }}
              onClick={() => {
                playSound('uiClick');
                host.scene?.setLightingMode?.(mode.id);
                host.forceUpdate();
              }}
            >
              {mode.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function renderDeckPicker(host) {
  if (!host.state.showDeckPicker) return null;
  return (
    <DeckSpawnDialog
      savedDecks={host.props.savedDecks}
      sorceryCards={host.props.sorceryCards}
      profile={host.props.profile}
      onSpawn={(deckId, playerNum) => host.loadAndSpawnDeck(deckId, playerNum)}
      onClose={() => host.setState({ showDeckPicker: false })}
    />
  );
}

export function renderBody(host) {
  const { onExit } = host.props;
  const { contextMenu } = host.state;
  // In casual sandbox, the displayed hand follows activePlayerNum.
  // In multiplayer mode this always resolves to the local player's
  // handCards. Legacy behaviour unchanged.
  const handCards = host.getActiveHandCards();

  const showControllerOverlay = isControllerEnabled() && host.inputSourceState === 'gamepad';
  const controllerAim = host.controllerAim;

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      onClick={() => {
        // Shift-click opens the pile-scry menu in a mousedown handler.
        // That left-click then bubbles up to this wrapper and would
        // immediately dismiss the menu, so the opener sets a one-shot
        // flag that we consume here.
        if (host._suppressNextRootClick) {
          host._suppressNextRootClick = false;
          return;
        }
        if (contextMenu) host.setState({ contextMenu: null });
      }}
    >
      {/* Loading overlay */}
      {host.state.isLoading || host.state.connectionFailed ? (
        <div className="fixed inset-0 z-[2000] bg-black flex items-center justify-center">
          <div className="flex flex-col items-center gap-4" style={{ zoom: host.state.viewScale }}>
            {host.state.connectionFailed ? (
              <>
                <div className="text-lg font-bold arena-heading" style={{ color: '#c45050' }}>{t('Connection Failed')}</div>
                <p className="text-sm text-center max-w-xs" style={{ color: TEXT_BODY }}>
                  {t('Unable to connect to the game server. Check your internet connection and try again.')}
                </p>
                <div className="flex gap-3 mt-2">
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-semibold cursor-pointer transition-all"
                    style={{ ...GOLD_BTN, borderRadius: 'var(--radius-lg)' }}
                    onClick={() => {
                      host.setState({ connectionFailed: false, isLoading: true, loadingMessage: t('Reconnecting...') });
                      host.initSession().then(async () => {
                        const { roomInfo } = host.state;
                        if (host.props.isArenaMatch && roomInfo?.hostDeck && roomInfo?.guestDeck) {
                          if (!roomInfo.resumed) {
                            try {
                              host.sync.withSuppressed(() => {
                                host.spawnSelectedDeck(roomInfo.hostDeck, 1);
                                host.spawnSelectedDeck(roomInfo.guestDeck, 2);
                              });
                            } catch {}
                          }
                        }
                        setTimeout(() => host.setState({ isLoading: false }), 500);
                      });
                    }}
                  >
                    {t('Retry')}
                  </button>
                  <button
                    type="button"
                    className="px-5 py-2 text-sm cursor-pointer transition-all"
                    style={{ ...BEVELED_BTN, borderRadius: 'var(--radius-lg)', color: TEXT_BODY }}
                    onClick={() => { if (host.props.onExit) host.props.onExit(); }}
                  >
                    {t('Back')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <RuneSpinner size={64} />
                <p className="text-sm" style={{ color: TEXT_BODY }}>{host.state.loadingMessage}</p>
                {host.state.canCancelJoin ? (
                  <button
                    type="button"
                    className="px-5 py-2 text-sm cursor-pointer transition-all mt-2"
                    style={{ ...BEVELED_BTN, borderRadius: 'var(--radius-lg)', color: TEXT_BODY }}
                    onClick={host.cancelJoinAttempts}
                  >
                    {t('Cancel')}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}

      {/* Floating sidebar buttons. Top left. Tailwind utilities here
          resolve through the cqw-redefined --spacing token (top-3,
          left-3, gap-2, size-10, rounded-xl, p-2.5, w-56, text-xs)
          so the whole sidebar tracks canvas scale. No zoom: needed.
          and zoom: would actively break popover-coordinate math per
          the project's UI scaling rules (CLAUDE.md). */}
      {/* ponytail: the combat prompt's modal backdrop (z-1099) covers the
          whole viewport, so during "Waiting for opponent..." the burger was
          unreachable. An opponent who quit mid-combat trapped the player
          with no way to Exit Game / End Match. Lift the sidebar over that
          backdrop only while a combat prompt is up; real modals sit at
          z-1200+ so they still win. */}
      <div data-tutorial="board-menu" className={`fixed top-3 left-3 ${host.state.cardActionMenu?.combatModal ? 'z-[1101]' : 'z-[1001]'} flex flex-col gap-2`}>
        {/* Burger menu */}
        <div className="relative">
          <button
            type="button"
            className="size-10 rounded-xl flex items-center justify-center cursor-pointer transition-all" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, color: TEXT_BODY }}
            onClick={() => { playSound('uiClick'); const wasOpen = host.state.showGameMenu; host.closeAllMenus(); host.setState({ showGameMenu: !wasOpen }); }}
            title={t('Menu')}
          >
            <svg style={{ width: '1.125cqw', height: '1.125cqw' }} viewBox="0 0 18 18" fill="none"><path d="M3 5h12M3 9h12M3 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
          {host.state.showGameMenu ? (
            <div className="absolute left-12 top-0 w-40 p-1.5" style={POPOVER_STYLE}>
              <FourCorners />
              {/* Same menu everywhere: Quick Save / Save As /
                  (dev) Spawn / End Match (arena only) / Settings
                  / Exit Game. No online toggles, no room code,
                  no spectator mode. Room provisioning is handled
                  server-side via the instance flow, and exit is
                  how the user leaves a session. */}
              {host.state.isHost ? (
                <>
                  <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.quickSave(); host.setState({ showGameMenu: false }); }}>
                    {t('Quick Save')}{host.state.currentSessionName ? ` (${host.state.currentSessionName})` : ''}
                  </button>
                  <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.openSaveDialog(); host.setState({ showGameMenu: false }); }}>
                    {t('Save As...')}
                  </button>
                </>
              ) : null}

              {isDevBuild() ? (
                <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.toggleSpawnEditor(); host.setState({ showGameMenu: false }); }}>
                  {host.state.isPlacingSpawns ? 'Done Placing' : 'Set Spawn Points'}
                </button>
              ) : null}

              {isDevBuild() ? (
                <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.toggleGridEditor(); host.setState({ showGameMenu: false }); }}>
                  {host.state.isEditingGrid ? 'Done Grid' : 'Set Grid'}
                </button>
              ) : null}

              {isDevBuild() ? (
                <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.toggleUiInversionEditor(); host.setState({ showGameMenu: false }); }}>
                  {host.state.isEditingUiInversion ? 'Done UI Inversion' : 'Set UI Inversion'}
                </button>
              ) : null}

              {isDevBuild() ? (
                <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.toggleComponentEditor(); host.setState({ showGameMenu: false }); }}>
                  {host.state.isEditingComponents ? 'Done Components' : 'Set Component Positions'}
                </button>
              ) : null}

              {/* Hidden for the spectate seat (canEndMatch). Exit Game is
                  still their way out of the board. */}
              {canEndMatch(host) && host.state.connectionStatus === 'connected' ? (
                <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: ACCENT_GOLD }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => host._clickEndMatch()}>
                  {t('End Match')}
                </button>
              ) : null}
              {canEndMatch(host) && host.state.connectionStatus !== 'connected' ? (
                <div className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-not-allowed" style={{ color: TEXT_MUTED }}>
                  {t('End Match (waiting for opponent)')}
                </div>
              ) : null}

              <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.setState({ showGameMenu: false }); if (host.props.onOpenSettings) host.props.onOpenSettings(); }}>
                {t('Settings')}
              </button>

              <div className="mx-1 my-1.5 h-px" style={{ background: `${GOLD} 0.08)` }} />

              <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs cursor-pointer" style={{ color: '#c45050' }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { host.requestExit(); host.setState({ showGameMenu: false }); }}>
                {t('Exit Game')}
              </button>
            </div>
          ) : null}
        </div>

        {/* Spawn Deck. Only in true single-player. Any active
            multiplayer connection (arena, invite, or casual sandbox)
            hides this button so one player can't wipe the shared
            board mid-match. */}
        {host.state.connectionStatus === 'offline' && !host.props.arenaSelectedDeckId && !host.state.roomInfo?.hostDeck && !host.props.isSpectating ? (
          <button
            type="button"
            className="size-10 rounded-xl flex items-center justify-center cursor-pointer transition-all" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, color: TEXT_BODY }}
            onClick={() => { playSound('uiClick'); host.closeAllMenus(); host.setState({ showDeckPicker: true }); }}
            title={t('Spawn Deck')}
          >
            <svg style={{ width: '1.125cqw', height: '1.125cqw' }} viewBox="0 0 18 18" fill="none"><rect x="3" y="2" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M6 7h6M6 10h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
          </button>
        ) : null}

        {/* Playmat picker */}
        <button
          type="button"
          className="size-10 rounded-xl flex items-center justify-center cursor-pointer transition-all" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, color: TEXT_BODY }}
          onClick={() => { playSound('uiClick'); const wasOpen = host.state.showPlaymatPicker; host.closeAllMenus(); host.setState({ showPlaymatPicker: !wasOpen }); }}
          title={t('Change Play Mat')}
        >
          <svg style={{ width: '1.125cqw', height: '1.125cqw' }} viewBox="0 0 18 18" fill="none"><rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2"/><path d="M5 6h8M5 9h8M5 12h8" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeDasharray="1.5 1.5"/></svg>
        </button>

        {/* Dice tool */}
        <div className="relative">
          <button
            type="button"
            className="size-10 rounded-xl flex items-center justify-center cursor-pointer transition-all" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, color: TEXT_BODY }}
            onClick={() => { playSound('uiClick'); const wasOpen = host.state.showDiceMenu; host.closeAllMenus(); host.setState({ showDiceMenu: !wasOpen }); }}
            title={t('Spawn Dice')}
          >
            <svg style={{ width: '1.125cqw', height: '1.125cqw' }} viewBox="0 0 18 18" fill="none"><rect x="2.5" y="2.5" width="13" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.5"/><circle cx="6" cy="6" r="1" fill="currentColor"/><circle cx="12" cy="6" r="1" fill="currentColor"/><circle cx="9" cy="9" r="1" fill="currentColor"/><circle cx="6" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>
          </button>
          <DiceTrayDrawer
            open={host.state.showDiceMenu}
            onClose={() => host.setState({ showDiceMenu: false })}
            onRig={host._setDiceTrayRig}
            onDragStart={host.beginDiceTrayDrag}
            onDragMove={host.moveDiceTrayDrag}
            onDragEnd={host.endDiceTrayDrag}
          />
        </div>

        {/* Ambient sound mixer */}
        <div className="relative">
          <button
            type="button"
            className="size-10 rounded-xl flex items-center justify-center cursor-pointer transition-all"
            style={{
              background: PANEL_BG,
              border: `1px solid ${GOLD} ${host.state.ambienceState.isPlaying ? '0.45)' : '0.2)'}`,
              color: host.state.ambienceState.isPlaying ? ACCENT_GOLD : TEXT_BODY,
            }}
            onClick={() => { playSound('uiClick'); const wasOpen = host.state.showAmbienceMenu; host.closeAllMenus(); host.setState({ showAmbienceMenu: !wasOpen }); }}
            title={t('Ambient Sound')}
          >
            <svg style={{ width: '1.125cqw', height: '1.125cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
            </svg>
          </button>
          {host.state.showAmbienceMenu ? (
            <div
              className="fixed inset-0"
              style={{ zIndex: 1001 }}
              onClick={() => host.setState({ showAmbienceMenu: false })}
            />
          ) : null}
          <AnimatePresence>
            {host.state.showAmbienceMenu ? (
              <AudioMixerPopover
                key="mixer"
                positionClassName=""
                anchor="left-center"
                positionStyle={{
                  position: 'fixed',
                  left: '4cqw',
                  top: '50%',
                  zIndex: 1002,
                }}
                extraAmbienceSection={host.renderLightingControls()}
                onClose={() => host.setState({ showAmbienceMenu: false })}
              />
            ) : null}
          </AnimatePresence>
        </div>

        {/* Connection status indicator */}
        {host.state.connectionStatus !== 'offline' ? (
          <div className="size-10 rounded-xl flex items-center justify-center" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.15)` }} title={host.state.connectionStatus}>
            <div className={cn('size-2.5 rounded-full', host.state.connectionStatus === 'connected' ? 'bg-green-400' : host.state.connectionStatus === 'waiting' ? 'bg-blue-400 animate-pulse' : 'bg-red-400')} />
          </div>
        ) : null}

      </div>

      {/* 3D Canvas. Opponent hand is rendered as 3D meshes on the
          table by updateOpponentHandMeshes(), driven by the same
          opponentHand state that the old 2D overlay used. */}
      {/* 3D Canvas */}
      <div className="relative flex-1 min-h-0">
        <canvas
          ref={host.canvasRef}
          className="block w-full h-full"
        />

        {/* Subsurface vignette. Radial darkening around the screen
            edges that reinforces "you are below the surface." Pure
            CSS (no per-frame work); opacity 0 ↔ 1 transitions in
            lockstep with the region-view toggle. z-index threads
            between world overlays (~50) and UI chrome (1000+). */}
        <div
          aria-hidden="true"
          className="region-view-vignette"
          data-active={host.state.regionView === 'subsurface' ? 'true' : undefined}
        />

        {/* Marquee drag-select overlay. Position-fixed with raw
            clientX/clientY; renderMarqueeOverlay mutates the style
            directly during the drag. Rounded corners + soft gold
            glow so it reads as a modern selection box rather than
            a bare rectangle. */}
        <div
          ref={host.marqueeOverlayRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            display: 'none',
            pointerEvents: 'none',
            border: '1.5px solid rgba(212, 168, 67, 0.9)',
            background: 'rgba(212, 168, 67, 0.1)',
            boxShadow: '0 0 16px rgba(212, 168, 67, 0.35), inset 0 0 12px rgba(212, 168, 67, 0.08)',
            borderRadius: 'var(--radius-lg)',
            zIndex: 50,
          }}
        />

        {/* Persistent "hug" outline around the current selection.
            Rendered in screen space by updateHugRect() on every
            frame so it tracks cards as they move. Purely
            decorative (pointer-events: none). The click hit-test
            for "click inside the hug zone to drag the selection"
            lives in handleMouseDown and reads host.hugRectScreen
            directly. */}
        <div
          ref={host.hugRectRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            display: 'none',
            pointerEvents: 'none',
            border: '1.5px solid rgba(212, 168, 67, 0.85)',
            background: 'rgba(212, 168, 67, 0.05)',
            boxShadow: '0 0 22px rgba(212, 168, 67, 0.28), inset 0 0 14px rgba(212, 168, 67, 0.08)',
            borderRadius: 'var(--radius-xl)',
            zIndex: 48,
            transition: 'border-color 120ms ease',
          }}
        />

        {/* Hover-only DOM badges shown next to the focused card.
            Behaves like a Google Maps pin. Constant pixel size at
            any camera zoom. So badges remain readable when zoomed
            out, where the world-space 3D badges become tiny.
            Position is mutated per frame by
            updateHoverStatusOverlayPosition(). */}
        <HoverStatusBadges
          hoverRef={host.hoverBadgesRef}
          statuses={host.state.hoverStatusOverlay?.statuses}
        />
        <HoverStatPills
          hoverRef={host.hoverStatPillsRef}
          attackDelta={host.state.hoverStatusOverlay?.attackDelta}
          defenseDelta={host.state.hoverStatusOverlay?.defenseDelta}
        />
        <EquipmentStacks
          entries={host.state.equipmentOverlays}
          registerRef={host.registerEquipmentOverlayRef}
        />
        <HoverEquippedCards
          hoverRef={host.hoverEquipmentRef}
          equippedCards={host.state.hoverStatusOverlay?.equippedCards}
          casterIds={Object.keys(host.state.gameState?.unitHands || {})}
        />
        <EquipPendingOverlays
          pendingIds={host.state.equipPending}
          registerRefs={host.registerEquipOverlayRef}
        />
        {/* Any cell-pick that carries projectilePaths renders the
            direction-trail overlays instead of plain cell targets.
            Flamecaller, Avatar of Fire, and the Ranged keyword's Shoot
            all share this (one projectile UX, keyed on the data, not
            an op allow-list). */}
        <CellTargetOverlays
          cellKeys={
            (host.state.pendingServerChoice?.kind === 'cell'
              ? host.state.pendingServerChoice.cells
              : null)
            || host.state.pendingSummonPlacement?.queue?.[0]?.validCells
            || (!host.state.pendingCellTarget?.projectilePaths
                ? host.state.pendingCellTarget?.validCells
                : null)
          }
          registerRefs={host.registerCellTargetOverlayRef}
        />
        <ProjectilePathOverlays
          paths={host.state.pendingCellTarget?.projectilePaths || null}
          registerRefs={host.registerProjectilePathOverlayRef}
        />
        <MovementReachOverlay
          cellKeys={host.dragging ? host.buildMovementReachCells() : null}
          harbingerCellKeys={host.dragging ? host.buildHarbingerReachCells() : null}
          dragonCellKeys={host.dragging ? host.buildDragonReachCells() : null}
          townCellKeys={host.dragging ? host.buildTownReachCells() : null}
          percevalCellKeys={host.dragging ? host.buildPercevalTargetCells() : null}
          registerRefs={host.registerMovementReachOverlayRef}
        />
        <InvalidCellHoverOverlay
          registerRef={(el) => { host._invalidCellHoverRef = el; }}
        />
        <CellElementPips
          cells={host.buildCellPipEntries()}
          registerRefs={host.registerCellPipOverlayRef}
        />
        <CellStackBadges
          badges={host.buildCellStackBadges()}
          registerRefs={host.registerCellStackBadgeOverlayRef}
          onOpen={host._openCellFocus}
        />
        <CellFanChrome
          open={!!host.state.focusedCell}
          registerRef={host.registerFanChromeRef}
        />
        {/* Replay feed: the cells of the cards the hovered row mentions.
            Positioned per frame by updateCellMentionOverlays. */}
        <CellMentionHighlights
          cellKeys={host.state.mentionHighlightCells}
          registerRefs={host.registerCellMentionOverlayRef}
        />
        {/* Cell → fan → act: hover affordance quad, positioned per frame by
            updateCellHoverHighlight (canvas-local transform). */}
        <div
          ref={(el) => { host._cellHoverHighlightRef = el; }}
          className="cell-hover-highlight"
          style={{ display: 'none' }}
          aria-hidden="true"
        />
        {/* Subsurface region tints are 3D meshes parented to
            scene.scene by _syncRegionViewTints. They sit between
            the playmat and the card layer, so no DOM overlay is
            needed here. */}

        <GameContextMenu
          contextMenu={host.state.contextMenu}
          actions={host.buildContextMenuActions()}
          viewScale={host.state.viewScale}
          onClose={host.closeContextMenu}
        />

        <CardActionMenu
          menu={host.state.cardActionMenu}
          viewScale={host.state.viewScale}
          onClose={host._closeCardActionMenu}
        />

        <CombatOverlay
          scene={host.state.combatScene}
          onConfirm={(map) => host._combatSubmitAssign(map)}
          onAmountsChange={host._broadcastAssignPreview}
          onLifeImpact={host._releaseLifeTick}
          onDone={host._closeCombatScene}
        />
        {/* The persistent combat progress strip: the fight's steps and its
            effect history, on screen from the first strike to completion
            while the large modal comes and goes. */}
        <CombatProgressPanel
          progress={host.state.combatProgress}
          localSeat={host._localSeat()}
          isSolo={!!host.props.isCasualSandbox}
          modalUp={!!host.state.combatScene}
        />

        {/* v1.43. Attack target modal. Renders nothing when
            attackTargetModal is null. Opens above the target cell
            after a Move-and-Attack drop or immediately on Attack-
            only. Cancel = abandon attack (move already landed,
            unit already tapped). */}
        <AttackTargetModal
          modal={host.state.attackTargetModal}
          viewScale={host.state.viewScale}
          logbookOpen={host.state.showGameLog}
          onPick={host._resolveAttackAction}
          onCancel={host._onAttackModalCancel}
        />

        {/* v1.51. Floating damage numbers above damaged cards.
            One per damage instance; auto-cleaned ~900ms later via
            onAnimationComplete. Renders nothing when the list is
            empty, so no DOM cost in non-combat moments. */}
        {(host.state.floatingDamage || []).map((d) => (
          <FloatingDamageNumber
            key={d.id}
            x={d.x}
            y={d.y}
            value={d.value}
            label={d.label}
            color={d.color}
            viewScale={host.state.viewScale}
            onComplete={() => host._removeFloatingDamage(d.id)}
          />
        ))}

        {host.state.equipModal ? (
          <EquipTargetModal
            source={host.state.equipModal.source}
            targets={host.state.equipModal.targets}
            onEquip={(target) => {
              host.equipCardTo(host.state.equipModal.source, target);
              host.closeEquipModal();
            }}
            onClose={host.closeEquipModal}
          />
        ) : null}

        <StatusRingMenu
          ringMenu={host.state.ringMenu}
          onToggle={(card, statusKey) => (statusKey === 'lance'
            ? host._toggleLanceEquip(card)
            : host.toggleCardStatus(card, statusKey))}
          onClearAll={(card) => host.clearAllStatuses(card)}
          onAdjustStat={(card, stat, action) => host.applyLifeButton({ action, stat, cardId: card.id })}
          onClose={() => host.setState({ ringMenu: null })}
          viewScale={host.state.viewScale}
        />

        {/* Codex + token ledge are play aids; a replay viewer changes
            nothing, so neither mounts (see the StatusTokenDrawer below). */}
        {host.props.replayDriver ? null : <GameCodexPanel
          open={host.state.showCodex}
          onToggle={() => {
            if (host.state.showCodex) {
              host.setState({ showCodex: false });
            } else {
              // Mutually exclusive with the status-token drawer:
              // if the drawer is already open the compendium
              // refuses to open. The user must close the drawer
              // first (chevron-click, Escape, or outside-click).
              if (host.state.statusTokenDrawerOpen) return;
              host.closeAllMenus();
              host.setState({ showCodex: true });
            }
          }}
        />}

        {(() => {
          // Banner follows the LOCAL player. In multiplayer each client
          // only owns its own mulligan (always the unsuffixed keys);
          // in casual sandbox the overlay swaps with Tab. Keying off
          // activePlayerNum here is wrong for multiplayer because it
          // tracks whose TURN it is. For the non-starting player it
          // would read the opponent's mulligan slot and hide the banner.
          const localPlayerNum = host._localPlayerNum();
          const keys = host._mulliganKeys(localPlayerNum);
          const stage = host.state[keys.phase];
          if (stage !== 'prompt' && stage !== 'selecting') return null;
          const avatarCard = host._findAvatarForPlayer(localPlayerNum);
          const maxCount = resolveAvatarRules(avatarCard).mulligan.maxCards;
          const flagged = host.state[keys.flags] || new Set();
          return (
            <MulliganOverlay
              ref={(ref) => { host.mulliganOverlayRef = ref; }}
              stage={stage}
              selectedCount={flagged.size}
              maxCount={maxCount}
              onAccept={host.acceptMulligan}
              onConfirm={host.executeMulligan}
              onKeep={host.keepHand}
            />
          );
        })()}

        {renderOracle(host)}
        {renderBattlemage(host)}
        {renderAlchemist(host)}

        {/* v1.111. Harbinger pre-game ceremony overlay. Only mounts
            while the divine sequence is running; unmounts (no exit
            tween) once the ledger entry has settled. */}
        {host.state.harbingerCeremony && (
          <HarbingerSetupOverlay
            playerLabel={host.state.harbingerCeremony.playerLabel}
            ownedByLocal={host.state.harbingerCeremony.ownedByLocal}
            canDecide={host.state.harbingerCeremony.canDecide}
            portraitUrl={host.state.harbingerCeremony.portraitUrl}
            phase={host.state.harbingerCeremony.phase}
            onAccept={host.acceptHarbingerCeremony}
            onCancel={host.cancelHarbingerCeremony}
          />
        )}

        {/* Casual sandbox HUD. Active-player pill + Start/End Game
            button. Hidden in every non-casual mode. The two sit
            side-by-side at the top so the user always knows (a)
            which side they're controlling and (b) whether the
            match has formally begun. */}
        {host.props.isCasualSandbox && (
          <div
            style={{
              position: 'fixed',
              top: '1cqw',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1050,
              display: 'flex',
              gap: '0.625cqw',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                padding: '0.5cqw 1cqw',
                borderRadius: 'var(--radius-lg)',
                background: 'rgba(12, 10, 8, 0.88)',
                border: `1px solid ${ACCENT_GOLD}`,
 boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
 color: TEXT_PRIMARY,
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.75cqw',
 fontWeight: 600,
 display: 'flex',
 alignItems: 'center',
 gap: '0.625cqw',
 cursor: 'pointer',
 userSelect: 'none',
 }}
 onClick={host.passTurn}
 title={t('Tab / End Turn to hand the turn to the other side')}
 >
 <span style={{ color: ACCENT_GOLD }}>● {t('Playing as')}</span>
 <span>{t('Player {n}', { n: host.state.activePlayerNum })}</span>
 <kbd style={{
 padding: '0.125cqw 0.375cqw',
 border: `1px solid ${GOLD} 0.4)`,
 borderRadius: 'var(--radius-sm)',
 fontSize: '0.625cqw',
 letterSpacing: 0,
 color: TEXT_MUTED,
 }}>TAB</kbd>
 </div>

 {(() => {
 // Solo is a real server match: no local Start/End lifecycle.
 // "End Game" leaves the table. The server pauses the instance,
 // and a fresh solo match starts from the Play screen.
 const endStyle = {
 padding: '0.5cqw 1.125cqw',
 borderRadius: 'var(--radius-lg)',
 boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.75cqw',
 fontWeight: 700,
 cursor: 'pointer',
 userSelect: 'none',
 transition: 'transform 0.15s, background 0.15s',
 background: 'linear-gradient(180deg, rgba(196,80,80,0.95), rgba(140,50,50,0.95))',
 border: '1px solid rgba(230, 136, 136, 0.6)',
 color: '#fdeaea',
 };
 return (
 <button
 type="button"
 style={endStyle}
 onClick={host.requestExit}
 onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
 onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
 title={t('Leave the table. The match is saved and can be resumed from the Play screen.')}
 >
 ◼ {t('End Game')}
 </button>
 );
 })()}
 </div>
 )}

 {/* First-run onboarding walkthrough. */}
 {host.state.showBoardTutorial && (
 <TutorialOverlay
 steps={BOARD_TUTORIAL_STEPS.map((s) =>
 s.key === 'region-view'
 ? { ...s, canAdvance: host.state.tutorialSubsurfaceVisited }
 : s,
 )}
 onDismiss={host.handleBoardTutorialDismiss}
 onStepChange={host.handleBoardTutorialStepChange}
 />
 )}

 {/* One-shot event tutorials (first unit played, expand button).
 Never rendered alongside the main walkthrough: the two overlays
 share the tutorial-spotlight-mask SVG id. */}
 {host.state.eventTutorial && !host.state.showBoardTutorial && (() => {
 const { key, ctx } = host.state.eventTutorial;
 const steps = key === CELL_STACK_TUTORIAL_KEY
 ? buildCellStackTutorialSteps({
 getBadgeRect: () => {
 const el = (ctx?.cellKey && host.cellStackBadgeOverlayRefs.get(ctx.cellKey))
 || host.cellStackBadgeOverlayRefs.values().next().value;
 return el?.getBoundingClientRect() || null;
 },
 })
 : buildFirstUnitTutorialSteps({
 getCardRect: () => {
 const mesh = ctx?.cardId ? host.meshes.get(ctx.cardId) : null;
 return mesh ? projectMeshScreenRect(host, mesh) : null;
 },
 });
 return (
 <TutorialOverlay steps={steps} mandatory onDismiss={host.handleEventTutorialDismiss} />
 );
 })()}

 {/* Player HUD. Vertical strip on the right */}
 {(() => {
 const isCasual = host.props.isCasualSandbox;
 // Casual sandbox: always list P1 then P2 (no "local vs.
 // opponent" distinction. One person drives both sides),
 // and End Turn is enabled whenever a game is in progress
 // rather than locked to a single side's turn.
 const localPlayer = isCasual
 ? (host.state.activePlayerNum === 2 ? 'p2' : 'p1')
 : (host.state.isHost ? 'p1' : 'p2');
 const opponentPlayer = localPlayer === 'p1' ? 'p2' : 'p1';
 const isMyTurn = isCasual
 ? host.state.matchPhase === 'playing'
 : host.state.currentTurn === localPlayer;
 const orderedPlayers = isCasual ? ['p1', 'p2'] : [localPlayer, opponentPlayer];
 // Shared dark blurred panel style. Used for the End Turn,
 // P1, and P2 panels so all three rounded rectangles read as
 // siblings of the same component family.
 const PANEL_BASE = {
 ...POPOVER_STYLE,
 background: 'rgba(12, 10, 8, 0.82)',
 borderRadius: 'var(--radius-xl)',
 boxShadow: '0 18px 48px rgba(0,0,0,0.4), 0 0 20px rgba(180,140,60,0.04)',
 backdropFilter: 'blur(10px)',
 WebkitBackdropFilter: 'blur(10px)',
 };
 const PANEL_BASE_SHADOW = '0 18px 48px rgba(0,0,0,0.4), 0 0 20px rgba(180,140,60,0.04)';
 // Elements share the same pip treatment used elsewhere in
 // the app (see ElementPip in DeckEditorSidebar). Tracker
 // keys use `wind`; the canonical element name is `Air`.
          const ELEMENT_COLORS = { earth: '#CFA572', water: '#01FFFF', fire: '#FF5F00', wind: '#A0BADB' };
          const elementKeys = ['earth', 'water', 'fire', 'wind'];
          const elementLabels = { earth: t('Earth'), water: t('Water'), fire: t('Fire'), wind: t('Air') };
          const renderElementPip = (el) => {
            const isUp = el === 'fire' || el === 'wind';
            const color = ELEMENT_COLORS[el];
            const points = isUp ? '5,1 9,9 1,9' : '5,9 1,1 9,1';
            const line = el === 'earth' ? { x1: 2, y1: 4, x2: 8, y2: 4 }
              : el === 'wind' ? { x1: 2, y1: 6, x2: 8, y2: 6 }
              : null;
            return (
              <svg style={{ width: '0.625cqw', height: '0.625cqw' }} viewBox="0 0 10 10" className="shrink-0">
                <polygon points={points} fill="none" stroke={color} strokeWidth="1.2" />
                {line ? <line {...line} stroke={color} strokeWidth="1.2" /> : null}
              </svg>
            );
          };
          // Every Sorcery avatar starts at the match's chosen HP
          // (10 for Quick Match, 20 for Standard). The HUD's "/N"
          // suffix and the in-match heal cap both consult this so
          // the two stay in sync. See incrementTracker for the
          // cap enforcement on the increment side.
          const AVATAR_MAX_LIFE = host.props.startingHp || 20;
          // Per-player HUD panel. Extracted so opponent and local
          // panels can be placed at opposite ends of the right-hand
          // strip (opponent top, local bottom) instead of stacked.
          const renderPlayerPanel = (player) => {
                  const tracker = host.state.gameState.trackers[player];
                  // Max mana = mana-producing sites + every active
                  // ledger GRANT_MANA contribution for this player.
                  // _stepProvideMana uses the same formula at turn
                  // start, so the displayed max stays in sync with
                  // the pool the engine actually grants.
                  const manaMax = Math.max(0, host.countSitesForPlayer(player) + host.effectLedger.manaContribution(player));
                  const isLocal = player === localPlayer;
                  // In casual sandbox profile info (discord handle,
                  // avatar picture) is irrelevant. Same account
                  // drives both sides. So we null it out and let
                  // the card-avatar lookup below supply display
                  // material. Multiplayer keeps the usual host /
                  // opponent profile pairing.
                  const info = isCasual ? null
                    : (isLocal ? host.props.arenaPlayerInfo : host.props.arenaOpponentInfo);
                  // In casual sandbox the HUD shows the *avatar card*
                  // on the board, not the account's profile picture.
                  // the same user is playing both sides, so a profile
                  // avatar tells you nothing about the deck. The card
                  // image doubles as a visual deck identifier.
                  const avatarCard = isCasual
                    ? host._findAvatarForPlayer(player === 'p2' ? 2 : 1)
                    : null;
                  const displayName = isCasual
                    ? (avatarCard?.name || PLAYER_LABELS[player])
                    : (info?.name || PLAYER_LABELS[player]);
                  const avatarUrl = isCasual
                    ? (avatarCard?.imageUrl ? resolveLocalImageUrl(avatarCard.imageUrl) : null)
                    : (info?.avatarUrl || null);
                  const isActive = host.state.currentTurn === player;
                  // Sorcery's Death's Door: an avatar at 0 life is
                  // alive but one shove from death. Flash the row
                  // hard so the player can't miss the threat.
                  // overrides the gold turn highlight when both
                  // are true (mortality > whose-turn-it-is).
                  const isDeathsDoor = (tracker.life ?? 0) <= 0;
                  const tintedBg = `linear-gradient(${GOLD} 0.16), ${GOLD} 0.16)), rgba(12, 10, 8, 0.82)`;
                  // Hand-pile counts. In casual sandbox both sides are
                  // local so we pull from handCards / handCards2; in
                  // multiplayer the opponent's hand is mirrored over
                  // the wire into `opponentHand` with isSite preserved.
                  const handForPanel = (() => {
                    if (host.props.isCasualSandbox) {
                      return player === 'p2' ? host.state.handCards2 : host.state.handCards;
                    }
                    return isLocal ? host.state.handCards : host.state.opponentHand;
                  })();
                  const atlasHandCount = Array.isArray(handForPanel)
                    ? handForPanel.filter((c) => c?.isSite).length
                    : 0;
                  const spellbookHandCount = Array.isArray(handForPanel)
                    ? handForPanel.length - atlasHandCount
                    : 0;
                  return (
                    <div
                      key={player}
                      data-tutorial={isLocal ? 'board-life-hud' : undefined}
                      data-hud-panel-for={player}
                      className={`relative w-[8.75cqw] flex flex-col items-center gap-1.5 px-3 py-3 transition-colors pointer-events-auto${isDeathsDoor ? ' vh-deaths-door-row' : ''}`}
                      style={{
                        ...PANEL_BASE,
                        // Active panels layer a translucent gold tint
                        // over the dark blurred panel base.
                        background: isDeathsDoor || !isActive ? PANEL_BASE.background : tintedBg,
                        // Border width is held constant regardless of
                        // turn / Death's Door state so the panel never
                        // reflows by 1–2px when the active player
                        // toggles. Only the colour switches.
                        border: (isDeathsDoor || !isActive)
                          ? '2px solid transparent'
                          : `2px solid ${ACCENT_GOLD}`,
                        // Compose the panel-base drop shadow with the
                        // active-turn glow so the panel keeps its
                        // floating-card depth on top of the highlight.
                        boxShadow: isDeathsDoor || !isActive
                          ? PANEL_BASE_SHADOW
                          : `${PANEL_BASE_SHADOW}, 0 0 18px ${GOLD} 0.35), inset 0 0 10px ${GOLD} 0.15)`,
                      }}
                    >
                      <FourCorners />
                      {isActive ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: '-0.5625cqw',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            padding: '0.125cqw 0.5cqw',
                            borderRadius: 'var(--radius-xl)',
                            background: `linear-gradient(180deg, ${ACCENT_GOLD}, rgba(158, 118, 46, 1))`,
 color: '#1a1208',
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.53cqw',
 fontWeight: 800,
 whiteSpace: 'nowrap',
 boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
 pointerEvents: 'none',
 }}
 >
 ◆ {t('Turn')}
 </div>
 ) : null}
 {(() => {
 // Resolve the user id to open as a profile
 // overlay. Casual sandbox runs on a single
 // account so both seats route to the local
 // user; multiplayer picks the seat's info. Replay
 // playback: the seats belong to the two people who
 // played, never to the viewer, so the local seat must
 // not fall back to the viewer's profile (QA#150).
 const profileTarget = isCasual
 ? host.props.profile?.id
 : (info?.id || (isLocal && !host.props.replayDriver ? host.props.profile?.id : null));
 const canOpen = !!profileTarget && typeof host.props.onViewProfile === 'function';
 const onOpen = canOpen ? () => host.props.onViewProfile(profileTarget) : null;
 return (
 <div
 className={canOpen ? 'relative cursor-pointer' : 'relative'}
 role={canOpen ? 'button' : undefined}
 tabIndex={canOpen ? 0 : undefined}
 onClick={onOpen || undefined}
 onKeyDown={canOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
 title={canOpen ? t('View profile') : undefined}
 >
 {avatarUrl ? (
 <AvatarImage src={avatarUrl} className="w-12 h-12 rounded-full" style={{ border: isActive ? `2px solid ${ACCENT_GOLD}` : `1px solid ${GOLD} 0.2)` }} />
                        ) : (
                          <div className="w-12 h-12 rounded-full flex items-center justify-center text-xs font-bold" style={isActive ? { background: `${GOLD} 0.15)`, border: `2px solid ${ACCENT_GOLD}`, color: ACCENT_GOLD } : { background: `${GOLD} 0.06)`, border: `1px solid ${GOLD} 0.2)`, color: TEXT_MUTED }}>
                            {isLocal ? t('You') : t('Opp')}
                          </div>
                        )}
                        {isDeathsDoor ? (
                          <div
                            className="vh-deaths-door-pill"
                            title={t("Death's Door. At 0 life, one more point of damage ends the match")}
                            style={{
                              position: 'absolute',
                              top: '-0.25cqw',
                              right: '-0.25cqw',
                              width: '1.375cqw',
                              height: '1.375cqw',
                              borderRadius: '50%',
                              background: 'radial-gradient(circle at 35% 30%, #ff7a72, #b21f1a 60%, #5f120f 100%)',
                              border: '1.5px solid rgba(255, 200, 195, 0.85)',
                              color: '#fff5f3',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1cqw',
                              lineHeight: 1,
                              textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
                              pointerEvents: 'none',
                              zIndex: 2,
                            }}
                            aria-label={t("Death's Door")}
                          >
                            ☠
                          </div>
                        ) : null}
                        {host.state.hudDamageFlash?.[player] ? (
                          <span
                            key={host.state.hudDamageFlash[player]}
                            className="vh-hud-damage-flash"
                            aria-hidden="true"
                          />
                        ) : null}
                          </div>
                        );
                      })()}
                      <div className="flex flex-col items-center gap-0.5 w-full min-w-0">
                        <span className="arena-heading text-sm max-w-full text-center truncate min-w-0" style={{ color: isActive ? TEXT_PRIMARY : TEXT_BODY }}>
                          {displayName}{(info?.id && host.state.hudPlayerTitle[player]) ? ',' : ''}
                        </span>
                        {info?.id ? (() => {
                          // Step down the title font size for longer
                          // titles so they fit on one line inside the
                          // narrow HUD panel. Steps are tuned for the
                          // italic display font at HUD width 8.75cqw.
                          const titleText = host.state.hudPlayerTitle[player];
                          const len = titleText ? titleText.length : 0;
                          const titleFontSize = len <= 14 ? '0.55cqw'
                            : len <= 18 ? '0.5cqw'
                            : len <= 22 ? '0.45cqw'
                            : '0.4cqw';
                          return (
                            <PlayerTitle
                              player={{ id: info.id, displayedCrownAvatarId: info.displayedCrownAvatarId, isFounder: !!info.isFounder, isQaTester: !!info.isQaTester }}
                              sorceryCards={host.props.sorceryCards}
                              prefix=""
                              style={{
                                display: 'block',
                                fontSize: titleFontSize,
                                lineHeight: 1.1,
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                color: isActive ? TEXT_PRIMARY : TEXT_BODY,
                                marginTop: '-0.15cqw',
                                marginBottom: '0.2cqw',
                              }}
                              onResolve={(_has, title) => {
                                const prev = host.state.hudPlayerTitle[player] || null;
                                const next = title || null;
                                if (prev === next) return;
                                host.setState((s) => ({
                                  hudPlayerTitle: { ...s.hudPlayerTitle, [player]: next },
                                }));
                              }}
                            />
                          );
                        })() : null}
                        {info?.discordHandle ? (
                          <button
                            type="button"
                            title={t('Copy @{handle} to clipboard', { handle: info.discordHandle })}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors cursor-pointer max-w-full"
                            style={{ background: 'rgba(88,101,242,0.12)', border: '1px solid rgba(88,101,242,0.3)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(88,101,242,0.2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(88,101,242,0.12)'; }}
                            onClick={() => {
                              navigator.clipboard.writeText(info.discordHandle)
                                .then(() => toast.success(t('Copied @{handle}. Paste in Discord search.', { handle: info.discordHandle })))
                                .catch(() => toast.error(t('Could not copy to clipboard')));
                            }}
                          >
                            <svg style={{ width: '0.625cqw', height: '0.625cqw' }} viewBox="0 0 24 24" fill="#5865F2" className="shrink-0">
                              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                            </svg>
                            <span className="text-[0.5625cqw] font-medium truncate" style={{ color: '#a8b4ff' }}>{info.discordHandle}</span>
                          </button>
                        ) : null}
                      </div>
                      <div className="flex justify-center w-full my-1.5">
                        <span className="text-[0.5625cqw] font-semibold leading-none" style={{ color: isActive ? ACCENT_GOLD : TEXT_MUTED }}>
                          {t('Turn {n}', { n: host.state.turnNumbers[player] || 0 })}
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-3 w-full">
                        <div
                          className={`flex items-baseline gap-1 ${host._canEditHudFor(player) ? 'vh-hud-adjustable' : ''}${host.state.hudPanel?.player === player && host.state.hudPanel?.kind === 'life' ? ' vh-hud-adjustable--open' : ''}`}
                          title={isDeathsDoor ? t("Death's Door. At 0 life, one more point of damage ends the match") : t('Life: {life} of {max}', { life: tracker.life, max: AVATAR_MAX_LIFE })}
                          data-buff-zone={`life-${player}`}
                          onClick={(e) => {
                            if (!host._canEditHudFor(player)) return;
                            e.stopPropagation();
                            // Active scroll preview on this row → commit instead of opening panel.
                            const s = host.state.hudScroll;
                            if (s && s.player === player && s.kind === 'life') {
                              host._commitHudScroll();
                              return;
                            }
                            // Toggle: clicking the same row while its
                            // panel is open closes the panel.
                            const open = host.state.hudPanel;
                            if (open && open.player === player && open.kind === 'life') {
                              host._closeHudPanel();
                              return;
                            }
                            host._openHudPanel(player, 'life');
                          }}
                          onWheel={(e) => host._handleHudScroll(player, 'life', e)}
                          onMouseLeave={() => {
                            const s = host.state.hudScroll;
                            if (s && s.player === player && s.kind === 'life') host._cancelHudScroll();
                          }}
                        >
                          <svg className={`size-3 self-center ${isDeathsDoor ? 'vh-deaths-door-heart' : 'text-red-400'}`} viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                          <HudLifeTicker
                            value={tracker.life}
                            className={`text-sm font-bold tabular-nums leading-none${isDeathsDoor ? ' vh-deaths-door-heart' : ''}`}
                            onDamage={() => host._flashHudDamage(player)}
                            inFight={!!(host.state.gameState?.pendingCombat || host.state.combatScene)}
                            release={host.state.hudLifeRelease?.[player] || 0}
                          />
                          <span className={`text-[0.625cqw] font-semibold tabular-nums opacity-60 leading-none${isDeathsDoor ? ' vh-deaths-door-heart' : ''}`}>/{AVATAR_MAX_LIFE}</span>
                        </div>
                        <div
                          className={`flex items-baseline gap-1 vh-hud-adjustable${host.state.hudPanel?.player === player && host.state.hudPanel?.kind === 'mana' ? ' vh-hud-adjustable--open' : ''}`}
                          title={t('Mana: {mana} of {max} (max = mana-producing sites in play)', { mana: tracker.mana, max: manaMax })}
                          data-buff-zone={`mana-${player}`}
                          onClick={(e) => {
                            // The mana panel is the buff inspector.
                            // it's openable for any seat, including
                            // opponent's in multiplayer. The panel
                            // itself reads canEdit to hide ✕ and
                            // creation affordances for non-owners.
                            e.stopPropagation();
                            const s = host.state.hudScroll;
                            if (s && s.player === player && s.kind === 'mana' && host._canEditHudFor(player)) {
                              host._commitHudScroll();
                              return;
                            }
                            const open = host.state.hudPanel;
                            if (open && open.player === player && open.kind === 'mana') {
                              host._closeHudPanel();
                              return;
                            }
                            host._openHudPanel(player, 'mana');
                          }}
                          onWheel={(e) => host._handleHudScroll(player, 'mana', e)}
                          onMouseLeave={() => {
                            const s = host.state.hudScroll;
                            if (s && s.player === player && s.kind === 'mana') host._cancelHudScroll();
                          }}
                        >
                          <svg className="size-3 self-center text-blue-400" viewBox="0 0 73 109" fill="currentColor"><path d="M68.0545 54.4998L36.4698 0L16.0701 35.2017L13.2228 40.1123L4.88575 54.5002C-1.62858 65.7451-1.62858 79.5956 4.88575 90.8352C11.4001 102.075 23.4412 109 36.4704 109C49.4996 109 61.5404 102.075 68.055 90.8352C71.2907 85.323 73 79.0531 73 72.6705C73 66.2825 71.29 60.0122 68.0545 54.4998Z" /></svg>
                          {(() => {
                            // Net GRANT_MANA contribution on this
                            // player. The MAX (right number) is the
                            // total the player can hold. That's
                            // what a buff/debuff actually changes,
                            // so it's the right place to surface
                            // the state. Positive → green, negative
                            // → red; opacity bumps to 1 either way
                            // so the colored cap reads as active
                            // rather than dimly muted. The left
                            // (available pool) stays neutral.
                            const buffTotal = host.effectLedger.manaContribution(player);
                            const maxColor = buffTotal > 0 ? '#7fe39a' : buffTotal < 0 ? '#ff8a78' : undefined;
                            const maxStyle = maxColor
                              ? {
                                  color: maxColor,
                                  opacity: 1,
                                  textShadow: buffTotal > 0
                                    ? '0 0 6px rgba(127,227,154,0.55)'
                                    : '0 0 6px rgba(255,138,120,0.5)',
                                }
                              : null;
                            return (
                              <>
                                <span className="text-sm font-bold tabular-nums leading-none">{tracker.mana}</span>
                                <span
                                  className="text-[0.625cqw] font-semibold tabular-nums leading-none opacity-60"
                                  style={maxStyle}
                                >
                                  /{manaMax}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 w-full pt-0.5">
                        {elementKeys.map((el) => (
                          <div key={el} className="flex items-center justify-center gap-1 px-1 py-0.5 rounded" style={{ background: `${GOLD} 0.04)` }} title={t('{element} affinity', { element: elementLabels[el] })}>
                            {renderElementPip(el)}
                            <span className="text-[0.625cqw] font-semibold tabular-nums" style={{ color: tracker[el] > 0 ? ELEMENT_COLORS[el] : TEXT_MUTED }}>{tracker[el] || 0}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-center gap-3 w-full">
                        <div className="flex items-baseline gap-1" title={t('Spellbook cards in hand: {n}', { n: spellbookHandCount })}>
                          <IconSpellbook className="size-3 self-center text-purple-300" />
                          <span className="text-sm font-bold tabular-nums leading-none">{spellbookHandCount}</span>
                        </div>
                        <div className="flex items-baseline gap-1" title={t('Atlas cards in hand: {n}', { n: atlasHandCount })}>
                          <IconAtlas className="size-3 self-center text-amber-300" />
                          <span className="text-sm font-bold tabular-nums leading-none">{atlasHandCount}</span>
                        </div>
                      </div>
                    </div>
                  );
          };
          // End Turn button. Sits beneath the bottom player's HUD.
          // i.e. the local player in multiplayer, or P1 in casual
          // sandbox where the user's seat is always pinned to P1.
          // The gold surface fills the full footprint so the entire
          // rectangle is the click target (no dead bezel).
          //
          // Depth comes from a layered concentric shadow stack. Gold
          // hairline rim → dark groove → outer warm halo → drop
          // shadow. Same forged-plate recipe used by MenuButton, but
          // tinted gold. The dark groove also keeps the plate
          // readable over light board backgrounds.
          const END_TURN_SHADOW_IDLE = [
            'inset 0 1px 0 rgba(255, 240, 200, 0.45)',
            'inset 0 -1px 0 rgba(60, 40, 10, 0.35)',
            'inset 0 0 22px rgba(120, 80, 20, 0.22)',
            '0 0 0 1px rgba(232, 195, 110, 0.75)',
            '0 0 0 3px rgba(0, 0, 0, 0.55)',
            '0 0 0 4px rgba(180, 140, 60, 0.28)',
            '0 6px 16px rgba(0, 0, 0, 0.55)',
          ].join(', ');
          const END_TURN_SHADOW_HOVER = [
            'inset 0 1px 0 rgba(255, 245, 215, 0.6)',
            'inset 0 -1px 0 rgba(60, 40, 10, 0.35)',
            'inset 0 0 26px rgba(160, 110, 35, 0.32)',
            '0 0 0 1px rgba(255, 220, 130, 0.9)',
            '0 0 0 3px rgba(0, 0, 0, 0.55)',
            '0 0 0 4px rgba(232, 195, 110, 0.5)',
            '0 8px 22px rgba(0, 0, 0, 0.6), 0 0 26px rgba(232, 195, 110, 0.28)',
          ].join(', ');
          const END_TURN_SHADOW_PRESS = [
            'inset 0 2px 4px rgba(60, 40, 10, 0.50)',
            'inset 0 -1px 0 rgba(255, 240, 200, 0.18)',
            'inset 0 0 30px rgba(120, 80, 20, 0.38)',
            '0 0 0 1px rgba(212, 168, 67, 0.7)',
            '0 0 0 3px rgba(0, 0, 0, 0.6)',
            '0 0 0 4px rgba(180, 140, 60, 0.22)',
            '0 2px 6px rgba(0, 0, 0, 0.55)',
          ].join(', ');
          // Engraved text effect. Bright bottom edge + dark top edge
          // makes the dark label look pressed into the gold surface.
          const END_TURN_TEXT_SHADOW = '0 1px 0 rgba(255, 240, 200, 0.5), 0 -1px 0 rgba(60, 40, 10, 0.4)';
          // Layered shading prepended onto GOLD_BTN's existing texture
          // stack. We use a single smooth vertical gradient (no
          // radial) so the gold reads as evenly lit from above rather
          // than spotlit. On a small button a radial creates an
          // unnatural hotspot. The bottom stops use warm browns
          // (rgba 60,35,10) rather than near-black so the lower edge
          // looks like ambient warm reflection, not pure shadow.
          //
          // Top layers are `tex-noise-panel.webp` stacked twice. The
          // same fine parchment grit used by the dice modal, ArenaHub
          // hero, and SkillTree screen, but doubled for density since
          // a single pass (with the SVG's baked-in 12% opacity)
          // disappears against the bright gold base. Both passes use
          // `multiply` blend. On a bright surface multiply darkens
          // where the texture has pixels, giving visible dark grit.
          //
          // We also promote GOLD_BTN's chisel/scratches/cracks layers
          // from `normal` to `overlay` so the wear marks underneath
          // become visible on the larger button surface (they were
          // tuned for tiny chip-sized GOLD_BTN consumers where
          // subtleties were imperceptible anyway).
          const END_TURN_BLEND = `multiply, multiply, normal, overlay, overlay, overlay, overlay, normal, normal, normal`;
          // GOLD_BTN's own gold gradient runs at 0.85–0.9 alpha, so the
          // board shows through it. The button is a primary action and
          // must never be see-through, so the same gradient is repeated
          // at full opacity as the bottom-most layer. Composite hue is
          // unchanged; only what's behind it stops leaking in.
          const END_TURN_GOLD_BASE = `linear-gradient(180deg, ${ACCENT_BRIGHT} 1) 0%, ${ACCENT} 1) 50%, ${ACCENT_LO} 1) 100%)`;
          const END_TURN_BG_IDLE = [
            'url("/tex-noise-panel.webp")',
            'url("/tex-noise-panel.webp")',
            'linear-gradient(180deg, rgba(255, 245, 215, 0.35) 0%, rgba(255, 240, 200, 0.10) 30%, transparent 55%, rgba(60, 35, 10, 0.22) 82%, rgba(60, 35, 10, 0.42) 100%)',
            GOLD_BTN.background,
            END_TURN_GOLD_BASE,
          ].join(', ');
          const END_TURN_BG_HOVER = [
            'url("/tex-noise-panel.webp")',
            'url("/tex-noise-panel.webp")',
            'linear-gradient(180deg, rgba(255, 250, 225, 0.48) 0%, rgba(255, 245, 210, 0.16) 30%, transparent 55%, rgba(95, 55, 12, 0.22) 82%, rgba(95, 55, 12, 0.40) 100%)',
            GOLD_BTN.background,
            END_TURN_GOLD_BASE,
          ].join(', ');
          const END_TURN_BG_PRESS = [
            'url("/tex-noise-panel.webp")',
            'url("/tex-noise-panel.webp")',
            'linear-gradient(180deg, rgba(60, 35, 10, 0.28) 0%, rgba(60, 35, 10, 0.12) 35%, transparent 60%, rgba(255, 240, 200, 0.10) 100%)',
            GOLD_BTN.background,
            END_TURN_GOLD_BASE,
          ].join(', ');
          // Disabled state. Same forged-plate vocabulary, desaturated
          // to cool stone-grey. Solid (not transparent) so it reads as
          // a tarnished iron version of the button instead of a faint
          // gold ghost on top of whatever's behind it.
          const END_TURN_DISABLED_BG = [
            'radial-gradient(ellipse 95% 65% at 50% 15%, rgba(210, 205, 195, 0.18) 0%, transparent 62%)',
            'linear-gradient(180deg, rgb(88, 84, 78) 0%, rgb(58, 55, 50) 50%, rgb(38, 36, 32) 100%)',
          ].join(', ');
          const END_TURN_DISABLED_SHADOW = [
            'inset 0 1px 0 rgba(255, 255, 255, 0.06)',
            'inset 0 -1px 0 rgba(0, 0, 0, 0.4)',
            'inset 0 0 24px rgba(0, 0, 0, 0.35)',
            '0 0 0 1px rgba(140, 132, 122, 0.32)',
            '0 0 0 3px rgba(0, 0, 0, 0.45)',
            '0 0 0 4px rgba(80, 75, 70, 0.16)',
            '0 4px 12px rgba(0, 0, 0, 0.45)',
          ].join(', ');
          const endTurnPanel = (
            <button
              type="button"
              className="arena-heading w-[8.75cqw] px-5 py-4 text-[1.375cqw] tracking-wide whitespace-nowrap cursor-pointer pointer-events-auto"
              style={isMyTurn ? {
                ...GOLD_BTN,
                background: END_TURN_BG_IDLE,
                backgroundBlendMode: END_TURN_BLEND,
                border: 'none',
                borderRadius: 'var(--radius-xl)',
                boxShadow: END_TURN_SHADOW_IDLE,
                textShadow: END_TURN_TEXT_SHADOW,
                transition: 'transform 80ms ease-out, box-shadow 80ms ease-out, background 80ms ease-out',
                transform: 'scale(1)',
              } : {
                background: END_TURN_DISABLED_BG,
                color: 'rgba(160, 152, 142, 0.85)',
                border: 'none',
                borderRadius: 'var(--radius-xl)',
                cursor: 'not-allowed',
                boxShadow: END_TURN_DISABLED_SHADOW,
                textShadow: '0 1px 0 rgba(0, 0, 0, 0.6)',
              }}
              onMouseEnter={isMyTurn ? (e) => {
                e.currentTarget.style.background = END_TURN_BG_HOVER;
                e.currentTarget.style.boxShadow = END_TURN_SHADOW_HOVER;
                e.currentTarget.style.transform = 'scale(1.015)';
              } : undefined}
              onMouseLeave={isMyTurn ? (e) => {
                e.currentTarget.style.background = END_TURN_BG_IDLE;
                e.currentTarget.style.boxShadow = END_TURN_SHADOW_IDLE;
                e.currentTarget.style.transform = 'scale(1)';
              } : undefined}
              onMouseDown={isMyTurn ? (e) => {
                e.currentTarget.style.background = END_TURN_BG_PRESS;
                e.currentTarget.style.boxShadow = END_TURN_SHADOW_PRESS;
                e.currentTarget.style.transform = 'scale(0.985)';
              } : undefined}
              onMouseUp={isMyTurn ? (e) => {
                e.currentTarget.style.background = END_TURN_BG_HOVER;
                e.currentTarget.style.boxShadow = END_TURN_SHADOW_HOVER;
                e.currentTarget.style.transform = 'scale(1.015)';
              } : undefined}
              disabled={!isMyTurn}
              onClick={host.passTurn}
            >
              <FitText>{t('End Turn')}</FitText>
            </button>
          );
          // v1.72 / v1.112. Region-view toggle. Vertical pill with two
          // halves: top = surface (sun icon), bottom = subsurface (cave
          // icon). Active half is gold-filled, inactive is muted. Click
          // a half to switch. Keyboard: Shift+W / Shift+S (remappable
          // via Settings → Keybinds → Camera).
          const regionViewPill = (() => {
            const isSub = host.state.regionView === 'subsurface';
            const surfaceHint = describeBinding(getBinding('view.surface'));
            const subsurfaceHint = describeBinding(getBinding('view.subsurface'));
            // Width matches the round pile buttons (size-16 = 4cqw)
            // so the toggle slots into the same column without
            // looking out of place. Each half is square (4cqw tall)
            // giving an overall 4cqw × 8cqw vertical pill. Clearly
            // "stacked options" against the circular siblings.
            const PILL_W = '4cqw';
            const HALF_H = '4cqw';
            const ICON_SIZE = '2cqw';
            return (
              <div
                data-tutorial="board-region-view"
                role="radiogroup"
                aria-label={t('Board view')}
                className="flex flex-col items-stretch overflow-hidden pointer-events-auto"
                style={{
                  width: PILL_W,
                  background: 'rgba(12, 10, 8, 0.82)',
                  border: `1px solid ${GOLD} 0.32)`,
                  borderRadius: `calc(${PILL_W} / 2)`,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.36), inset 0 0 4px rgba(0,0,0,0.22)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <Tooltip content={t('Show surface ({key})', { key: surfaceHint })} side="left">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isSub}
                    onClick={() => {
                      if (host.state.regionView !== 'surface') host.setState({ regionView: 'surface' });
                    }}
                    className="flex items-center justify-center cursor-pointer transition-colors"
                    style={{
                      height: HALF_H,
                      background: !isSub ? `${GOLD} 0.22)` : 'transparent',
                      color: !isSub ? ACCENT_GOLD : TEXT_BODY,
                      boxShadow: !isSub ? `inset 0 0 10px ${GOLD} 0.22)` : 'none',
                    }}
                  >
                    <SunIcon size={ICON_SIZE} />
                  </button>
                </Tooltip>
                <div style={{ height: '1px', background: `${GOLD} 0.32)` }} />
                <Tooltip content={t('Show subsurface ({key})', { key: subsurfaceHint })} side="left">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSub}
                    onClick={() => {
                      if (host.state.regionView !== 'subsurface') host.setState({ regionView: 'subsurface' });
                    }}
                    className="flex items-center justify-center cursor-pointer transition-colors"
                    style={{
                      height: HALF_H,
                      background: isSub ? `${GOLD} 0.22)` : 'transparent',
                      color: isSub ? ACCENT_GOLD : TEXT_BODY,
                      boxShadow: isSub ? `inset 0 0 10px ${GOLD} 0.22)` : 'none',
                    }}
                  >
                    <CaveIcon size={ICON_SIZE} />
                  </button>
                </Tooltip>
              </div>
            );
          })();
          // Off-board zones. Collection, Banished and the token
          // spawner. Consolidated behind a single fan-out trigger
          // ("Zones") to save HUD space. Each item opens / toggles a
          // searchPile overlay; the fan order maps to the mockup
          // (Collection up-left, Banished left, Tokens down-left).
          const pileButtons = (() => {
            const banishedId = localPlayer === 'p2' ? 'banished-p2' : 'banished-p1';
            // Always offer Collection. Clicking lazily materializes an
            // empty pile when the deck declared no collection zone, so
            // it stays a stable "put this in Collection" hook.
            const collectionPile = host.findLocalPileByName('Collection');
            const iconStyle = { width: '2.75cqw', height: '2.75cqw', pointerEvents: 'none' };
            const items = [
              {
                key: 'collection',
                label: t('Collection'),
                hint: describeBinding(getBinding('pile.searchCollection')),
                isActive: !!(collectionPile && host.state.searchPile?.id === collectionPile.id),
                onSelect: () => {
                  const pile = collectionPile || host.findOrCreateCollection();
                  if (host.state.searchPile?.id === pile.id) host.handlePileSearchClose();
                  else host.openPileSearch(pile);
                },
                // Visibility toggle is always offered. ToggleCollectionRevealed
                // create-or-finds the pile, so the player can reveal their
                // Collection to the opponent even before they've opened it.
                accessory: (
                  <CollectionVisibilityBadge
                    isPublic={!!collectionPile?.revealed}
                    onToggle={host.toggleCollectionRevealed}
                  />
                ),
                icon: (
                  /* Card-exchange. Delapouite, game-icons.net (CC BY 3.0) */
                  <svg style={iconStyle} viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
                    <path d="M258.148 20.822c-1.112.008-2.226.026-3.343.055-39.32 1.041-81.507 15.972-123.785 50.404l-6.028 4.91-5.732-5.25c-12.644-11.578-20.276-27.633-25.653-43.716-8.974 36.98-14.631 81.385-9.232 114.523 18.065.908 45.409-2.177 73.7-7.818 17.858-3.561 36.048-8.126 53.064-13.072-13.419-2.911-25.896-6.882-38.143-12.082l-16.088-6.832 14.906-9.127c46.367-28.393 80.964-40.686 120.235-35.553 33.105 4.327 69.357 20.867 119.066 47.271-25.373-36.314-62.243-64.737-104.728-76.994-15.402-4.443-31.553-6.828-48.239-6.719zM346 116c-46.667 0-46.666 0-46.666 46.666V349.4c0 9.596.007 17.19.414 23.242a664.804 664.804 0 0 1 50.656-12.223c24.649-4.915 48.367-8.224 67.916-8.41 6.517-.062 12.571.224 18.041.912l6.31.793 1.358 6.213c2.464 11.265 3.673 23.447 3.914 36.059 38.032-.19 38.057-3.06 38.057-46.65V162.665C486 116 486 116 439.334 116a226.98 226.98 0 0 1 3.978 7.64l12.624 25.536-25.004-13.648c-13.085-7.143-25.164-13.632-36.452-19.528zm-281.943.016c-38.032.19-38.057 3.06-38.057 46.65V349.4C26 396 26 396 72.666 396a226.98 226.98 0 0 1-3.978-7.64l-12.624-25.536 25.004 13.649c13.085 7.142 25.164 13.632 36.452 19.527H166c46.667 0 46.666 0 46.666-46.666V162.666c0-9.626-.006-17.24-.416-23.304a664.811 664.811 0 0 1-50.654 12.22c-32.865 6.554-64.077 10.25-85.957 7.498l-6.31-.793-1.358-6.213c-2.464-11.265-3.673-23.446-3.914-36.058zm354.619 254.078c-17.543.25-40.826 3.206-64.75 7.977-17.859 3.56-36.05 8.125-53.065 13.072 13.419 2.91 25.896 6.881 38.143 12.082l16.088 6.832-14.906 9.127c-46.367 28.392-80.964 40.685-120.235 35.553-33.105-4.327-69.357-20.868-119.066-47.272 25.373 36.315 62.243 64.738 104.728 76.994 52.573 15.166 113.872 6.343 175.367-43.74l6.028-4.91 5.732 5.25c12.644 11.579 20.276 27.633 25.653 43.717 8.974-36.981 14.631-81.386 9.232-114.524-2.788-.14-5.748-.204-8.95-.158z" />
                  </svg>
                ),
              },
              {
                key: 'banished',
                label: t('Banished'),
                hint: describeBinding(getBinding('pile.searchBanished')),
                isActive: host.state.searchPile?.id === banishedId,
                onSelect: () => {
                  // Eagerly materialize both players' piles so the
                  // dialog's tab bar always has both targets.
                  host.findOrCreateBanished(false);
                  host.findOrCreateBanished(true);
                  const pile = host.findOrCreateBanished(host.getActivePlayerRotated());
                  if (host.state.searchPile?.id === pile.id) host.handlePileSearchClose();
                  else host.openPileSearch(pile);
                },
                icon: (
                  /* Concentric-crescents portal. Lorc, game-icons.net (CC BY 3.0) */
                  <svg style={iconStyle} viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
                    <path d="M251.19 25.684c-127.62 0-231.272 103.653-231.272 231.27 0 127.62 103.653 231.275 231.27 231.275 127.62 0 231.275-103.657 231.275-231.275 0-127.618-103.656-231.27-231.274-231.27zm0 18.693c117.515 0 212.58 95.063 212.58 212.578s-95.065 212.58-212.58 212.58c-89.756 0-166.407-55.458-197.616-134.017 33.247 63.767 99.96 107.316 176.842 107.316l-.002.004c110.068 0 199.316-89.248 199.316-199.317 0-106.046-82.85-192.754-187.345-198.948 2.92-.118 5.854-.195 8.804-.195zm-25.362 1.508c83.624 11.428 148.188 83.327 148.188 170.062 0 94.667-77.078 171.172-171.746 171.172-76.475 0-141.293-50.118-163.385-119.288a217.99 217.99 0 0 1-.274-10.877c0-14.34 1.426-28.34 4.124-41.877 1.82 83.566 70.14 150.768 154.145 150.768 3.84 0 7.782-.17 11.552-.446-68.53-5.156-122.64-62.572-122.64-132.412 0-73.23 59.625-132.412 132.858-132.412 68.655 0 125.172 52.213 131.97 119.082.15-2.702.444-5.262.444-8 0-85.145-69.038-154.185-154.183-154.185-8.306 0-16.454.663-24.403 1.927a211.376 211.376 0 0 1 53.35-13.515zm-6.955 73.553c-2.86 0-5.794.132-8.6.332 51.024 3.838 91.31 46.586 91.31 98.587 0 54.524-44.395 98.588-98.92 98.588-51.118 0-93.194-38.875-98.257-88.664-.112 2.012-.332 3.92-.332 5.956 0 63.396 51.404 114.8 114.8 114.8 63.394 0 114.798-51.404 114.798-114.8 0-63.395-51.404-114.798-114.8-114.798zm-23.328 11.13-.002.002c-44.363 0-80.336 35.973-80.336 80.336s35.973 80.334 80.336 80.334c2 0 4.055-.094 6.02-.232-35.705-2.686-63.897-32.602-63.897-68.99 0-38.155 31.066-68.99 69.223-68.99 35.77 0 65.215 27.2 68.758 62.042.075-1.407.23-2.74.23-4.166 0-44.363-35.97-80.336-80.333-80.336z" />
                  </svg>
                ),
              },
              {
                key: 'tokens',
                label: t('Tokens'),
                hint: describeBinding(getBinding('game.tokenBrowser')),
                isActive: host.state.searchPile?.id === 'token-pile',
                onSelect: () => {
                  if (host.state.searchPile?.id === 'token-pile') host.handlePileSearchClose();
                  else host.openPileSearch(host.buildTokenPile());
                },
                icon: (
                  /* Card bearing a plus. Spawn a generated card token */
                  <svg style={iconStyle} viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="5" y="3" width="14" height="18" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                    <path d="M12 8.2 V15.8 M8.2 12 H15.8" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                  </svg>
                ),
              },
            ];
            return <PileFanMenu items={items} triggerLabel={t('Zones')} />;
          })();
          // Spatial HUD layout. The player whose seat is at the
          // bottom of the virtual table gets the bottom panel, the
          // counterpart gets the top panel. In casual sandbox the
          // camera is fixed (no 180° rotation) so P1 always sits at
          // the bottom and P2 at the top regardless of which side is
          // currently being piloted; the active-turn highlight follows
          // the turn pill, not the panel position. In multiplayer the
          // local player anchors the bottom and the opponent. Whose
          // table is rotated 180°. Anchors the top.
          const bottomPlayer = isCasual ? 'p1' : localPlayer;
          const topPlayer = isCasual ? 'p2' : opponentPlayer;
          // While the combat overlay is on stage, the HUD strip rides
          // ABOVE its dark backdrop. The player panels (hearts, life
          // tickers, avatar damage flashes) are part of the combat
          // presentation and must stay readable. The non-vital strip
          // chrome (zone buttons, end turn) is dimmed to match the
          // backdrop and made inert so the lift can't make anything
          // newly clickable through the cinematic.
          const combatLift = !!host.state.combatScene;
          const dimmedChrome = combatLift
            ? { filter: 'brightness(0.4) saturate(0.85)', transition: 'filter 200ms ease' }
            : { transition: 'filter 200ms ease' };
          // Replay playback: the HUD stays visible (life/mana/thresholds are
          // part of reading the match) but nothing is actionable. The end-turn
          // button and the Zones drawer are meaningless for a viewer and hide
          // entirely; the region-view pill stays interactive (it only changes
          // what the viewer looks at, not the match).
          const isReplay = !!host.props.replayDriver;
          return (
            <div
              className="absolute top-3 right-3 bottom-3 flex flex-col items-end justify-between pointer-events-none"
              style={{ zIndex: combatLift ? 1210 : 10 }}
            >
              <div inert={isReplay || undefined}>
                {renderPlayerPanel(topPlayer)}
              </div>
              <div className="flex flex-col items-end gap-2 pointer-events-none" inert={combatLift || undefined} style={dimmedChrome}>
                {regionViewPill}
                {isReplay ? null : (
                  <div data-tutorial="board-pile-buttons" className="flex flex-col items-end gap-2">
                    {pileButtons}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 pointer-events-none">
                <div inert={isReplay || undefined}>
                  {renderPlayerPanel(bottomPlayer)}
                </div>
                {isReplay ? null : (
                  <div className="flex flex-col items-end gap-2 pointer-events-none" inert={combatLift || undefined} style={dimmedChrome}>
                    {endTurnPanel}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Player badges are 3D sprites in the scene. See updatePlayerBadges() */}

        {host.state.isEditingComponents ? (() => {
          const placements = host.playmatComponentEditor?.getPlacements() || {};
          const draggingKey = host.playmatComponentEditor?.draggingKey || null;
          const draggingDef = draggingKey ? COMPONENT_KEYS.find((d) => d.key === draggingKey) : null;
          const draggingPlacement = draggingKey ? placements[draggingKey] : null;
          return (
            <div className="absolute top-2 left-2 z-10 w-72 p-2" style={{ ...POPOVER_STYLE, zoom: host.state.viewScale }}>
              <div className="mb-2 text-[0.625cqw] font-semibold" style={SECTION_HEADER_STYLE}>Component Positions</div>
              <p className="mb-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                Drag any of the 12 components into position. P2 components are pre-rotated 180° so you see what P2 sees. Copy JSON when done and paste into uiOverlayGenerator.js → COMPONENT_PLACEMENTS.
              </p>
              <div className="mb-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                {Object.keys(placements).length} of {COMPONENT_KEYS.length} placed
                {draggingDef ? (
                  <>
                    {' · '}
                    <span style={{ color: ACCENT_GOLD }}>{draggingDef.label}</span>
                    {draggingPlacement ? (
                      <span> ({draggingPlacement.x.toFixed(1)}, {draggingPlacement.z.toFixed(1)})</span>
                    ) : null}
                  </>
                ) : null}
              </div>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                  style={{ color: TEXT_BODY }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={host.copyComponentPlacementsJSON}
                >
                  Copy JSON
                </button>
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                  style={{ color: ACCENT_GOLD }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={host.toggleComponentEditor}
                >
                  Done
                </button>
              </div>
            </div>
          );
        })() : null}

        {host.state.isEditingUiInversion ? (() => {
          const editor = host.uiInversionEditor;
          const regions = editor?.getRegions() || [];
          const activeIndex = editor?.activeIndex ?? -1;
          const hasRegions = regions.length > 0;
          const hasActive = activeIndex >= 0 && activeIndex < regions.length;
          const mode = editor?.mode;
          return (
            <div className="absolute top-2 left-2 z-10 w-64 p-2" style={{ ...POPOVER_STYLE, zoom: host.state.viewScale }}>
              <div className="mb-2 text-[0.625cqw] font-semibold" style={SECTION_HEADER_STYLE}>UI Inversion Regions</div>
              <div className="mb-1 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>Theme: <span style={{ color: ACCENT_GOLD }}>{getCurrentPlaymatThemeId()}</span></div>
              <p className="mb-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                {mode === 'drag-start' || mode === 'drag-end'
                  ? 'Click and drag on the mat to draw a region.'
                  : hasRegions
                    ? 'Click a region to select. Drag its body to move, drag a handle to resize.'
                    : 'Click and drag on the mat to draw the first region.'}
              </p>
              <div className="mb-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                {hasRegions ? `${regions.length} region${regions.length === 1 ? '' : 's'}` : 'No regions yet'}
                {hasActive ? ` · active #${activeIndex + 1}` : ''}
              </div>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                  style={{ color: TEXT_BODY }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={host.addUiInversionRegion}
                >
                  Add region
                </button>
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ color: TEXT_BODY }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={host.deleteActiveUiInversionRegion}
                  disabled={!hasActive}
                >
                  Delete selected region
                </button>
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ color: TEXT_BODY }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={host.copyUiInversionJSON}
                  disabled={!hasRegions}
                >
                  Copy JSON
                </button>
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                  style={{ color: ACCENT_GOLD }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={host.toggleUiInversionEditor}
                >
                  Done
                </button>
              </div>
            </div>
          );
        })() : null}

        {host.state.isEditingGrid ? (
          <div className="absolute top-2 left-2 z-10 w-56 p-2" style={{ ...POPOVER_STYLE, zoom: host.state.viewScale }}>
            <div className="mb-2 text-[0.625cqw] font-semibold" style={SECTION_HEADER_STYLE}>Playmat Grid</div>
            <p className="mb-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
              {getGameGrid(host.state.spawnConfig)
                ? 'Drag any corner or divider handle to adjust the grid. Click "Copy JSON" when done.'
                : 'Click and drag on the table to draw the grid bounds.'}
            </p>
            <div className="flex flex-col gap-1">
              <button
                type="button"
                className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                style={{ color: TEXT_BODY }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onClick={host.copyGridJSON}
              >
                Copy JSON
              </button>
              <button
                type="button"
                className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                style={{ color: TEXT_BODY }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onClick={host.clearGrid}
              >
                Clear grid
              </button>
              <button
                type="button"
                className="rounded-lg px-2 py-1.5 text-xs cursor-pointer text-left"
                style={{ color: ACCENT_GOLD }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                onClick={host.toggleGridEditor}
              >
                Done
              </button>
            </div>
          </div>
        ) : null}

        {host.state.isPlacingSpawns ? (
          <div className="absolute top-2 left-2 z-10 w-56 max-h-[80vh] overflow-y-auto p-2" style={{ ...POPOVER_STYLE, zoom: host.state.viewScale }}>
            <div className="mb-2 text-[0.625cqw] font-semibold" style={SECTION_HEADER_STYLE}>Pile Spawn Points</div>
            {Object.entries(SPAWN_LABELS).map(([key, label]) => {
              const isActive = host.state.activeSpawnKey === key;
              const hasPoint = host.state.spawnConfig[key];
              const color = SPAWN_COLORS[key];
              return (
                <button
                  key={key}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors cursor-pointer"
                  style={isActive ? { background: `${GOLD} 0.15)`, color: TEXT_PRIMARY } : { color: TEXT_BODY }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => {
                    host.setState({ activeSpawnKey: isActive ? null : key, trackerEditing: null });
                    host.clearTrackerPreviews();
                    host.hideTrackerCursorPreview();
                  }}
                >
                  <span className="size-2.5 rounded-full" style={{ backgroundColor: color }} />
                  <span className="flex-1 text-left">{label}</span>
                  {hasPoint ? <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>set</span> : null}
                </button>
              );
            })}
            {host.state.activeSpawnKey ? (
              <div className="mt-2 text-[0.625cqw]" style={{ color: ACCENT_GOLD }}>Click on the table to place {SPAWN_LABELS[host.state.activeSpawnKey]}</div>
            ) : null}

            <div className="mt-3 mb-2 pt-2 text-[0.625cqw] font-semibold" style={{ borderTop: `1px solid ${GOLD} 0.1)`, ...SECTION_HEADER_STYLE }}>Tracker Spawn Points</div>
            {getTrackerSpawnEntries().map((entry) => {
              const te = host.state.trackerEditing;
              const isActive = te && te.trackerKey === entry.trackerKey && te.player === entry.player;
              const configured = isTrackerConfigured(host.state.spawnConfig, entry.player, entry.trackerKey, TRACKER_DEFS[entry.trackerKey]);
              return (
                <button
                  key={entry.spawnKey}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors cursor-pointer"
                  style={isActive ? { background: `${GOLD} 0.15)`, color: ACCENT_GOLD } : { color: TEXT_BODY }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  onClick={() => {
                    host.clearTrackerPreviews();
                    if (isActive) {
                      host.hideTrackerCursorPreview();
                      host.setState({ trackerEditing: null, activeSpawnKey: null });
                    } else {
                      host.showTrackerCursorPreview();
                      host.setState({
                        trackerEditing: { trackerKey: entry.trackerKey, player: entry.player, flatIndex: 0 },
                        activeSpawnKey: null,
                      });
                    }
                  }}
                >
                  <span className="size-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="flex-1 text-left">{entry.label}</span>
                  {configured ? <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>set</span> : null}
                </button>
              );
            })}
            {host.state.trackerEditing ? (
              <div className="mt-2 text-[0.625cqw]" style={{ color: ACCENT_GOLD }}>
                {getTrackerProgressLabel(host.state.trackerEditing.trackerKey, host.state.trackerEditing.player, host.state.trackerEditing.flatIndex)}
                <br />Click on the table to set this position.
              </div>
            ) : null}

          </div>
        ) : null}
        {host.renderDeckPicker()}

        {/* Playmat picker */}
        {host.state.showPlaymatPicker ? (
          <PlaymatPicker
            onClose={() => host.setState({ showPlaymatPicker: false })}
            onChangePlaymat={(url) => {
              // The vanilla URL tracks the user's grid settings too
              // (line style, faded, fully-numbered), so re-push it
              // any time the playmat picker mutates anything.
              host.scene?.setPlaymat(url);
              host.scene?.setVanillaPlaymat?.(getVanillaPlaymatUrl());
            }}
            viewScale={host.state.viewScale}
          />
        ) : null}

        {/* Shortcut help overlay. Toggled by pressing ? */}
        {host.state.showShortcutHelp ? (
          <div
            className="fixed inset-0 z-[1200] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={() => host.setState({ showShortcutHelp: false })}
          >
            <div
              className="relative"
              style={{
                width: 560,
                zoom: host.state.viewScale,
                background: `url("/tex-noise-panel.webp"), #0e0a06`,
                border: `1px solid ${GOLD} 0.22)`,
                borderRadius: 'var(--radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 32px rgba(180,140,60,0.08)',
                padding: '20px 22px 18px',
                isolation: 'isolate',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <FourCorners />
              <div className="flex items-center justify-between mb-3">
                <h2
                  className="text-lg font-bold arena-heading"
                  style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
                >
                  {t('Keyboard Shortcuts')}
                </h2>
                <CloseButton onClick={() => host.setState({ showShortcutHelp: false })} />
              </div>
              <ShortcutList />
            </div>
          </div>
        ) : null}

        <PileSearchDialog
          pile={host.state.searchPile}
          query={host.state.searchQuery}
          sorceryCards={host.props.sorceryCards}
          readOnly={(() => {
            const pile = host.state.searchPile;
            if (!pile) return false;
            // Spectators are read-only in every dialog they could
            // somehow end up seeing (e.g. a public-pile peek wired
            // up before this gate).
            if (host.props.isSpectating) return true;
            // A cemetery-spanning multi-select (Return to Nature, Sphinx
            // Sarcophagus) picks "from a cemetery." EITHER player's,
            // including the opponent's. So neither pile is read-only
            // while that session is open.
            if (host.state.pendingPileSelect?.spanCemeteries && pile.name?.startsWith('Cemetery')) return false;
            const isCem = pile.name?.startsWith('Cemetery');
            const isBanished = pile.name?.startsWith('Banished');
            const isCollection = pile.name === 'Collection';
            if (!isCem && !isBanished && !isCollection) return false;
            // In casual sandbox the user controls both sides, so every
            // pile is editable regardless of rotation.
            if (host.props.isCasualSandbox) return false;
            if (host.state.connectionStatus === 'offline') return false;
            return !!pile.rotated !== !host.state.isHost;
          })()}
          tabs={(() => {
            const pile = host.state.searchPile;
            if (!pile) return null;
            // Imposter Mask locks the picker to the controller's own
            // Collection. No opponent tab, no tab bar at all. The
            // entire dialog exists to resolve the pick.
            if (host.state.pendingMaskPick) return null;
            const localRotated = host.getActivePlayerRotated();
            const piles = host.state.gameState.piles;
            if (pile.name?.startsWith('Banished')) {
              const p1 = piles.find((p) => p.id === 'banished-p1');
              const p2 = piles.find((p) => p.id === 'banished-p2');
              if (!p1 || !p2) return null;
              const buildTab = (target, rotated) => ({
                pile: target,
                label: `${PLAYER_LABELS[rotated ? 'p2' : 'p1']} (${target.cards.length})`,
                readOnly: host.props.isSpectating
                  ? true
                  : host.props.isCasualSandbox
                    ? false
                    : (host.state.connectionStatus !== 'offline' && rotated !== localRotated),
              });
              return [buildTab(p1, false), buildTab(p2, true)];
            }
            // A cemetery-spanning multi-select (Return to Nature, Sphinx
            // Sarcophagus) pulls "from a cemetery" (either player's).
            // Surface BOTH Cemetery piles as tabs so the player can flip
            // between their own and the opponent's. Selecting a tab swaps
            // searchPile (openPileSearch) without disturbing
            // pendingPileSelect, so the running selection survives the flip.
            if (host.state.pendingPileSelect?.spanCemeteries && pile.name?.startsWith('Cemetery')) {
              const own = piles.find((p) => p.name?.startsWith('Cemetery') && !!p.rotated === localRotated);
              const opp = piles.find((p) => p.name?.startsWith('Cemetery') && !!p.rotated !== localRotated);
              const buildTab = (target, rotated) => ({
                pile: target,
                label: `${PLAYER_LABELS[rotated ? 'p2' : 'p1']} (${target.cards.length})`,
                readOnly: false,
              });
              const tabs = [];
              if (own) tabs.push(buildTab(own, localRotated));
              if (opp) tabs.push(buildTab(opp, !localRotated));
              return tabs.length > 1 ? tabs : null;
            }
            if (pile.name === 'Collection') {
              // Surface the opponent's Collection as a second tab only
              // when they've toggled their "eye" on. In casual sandbox
              // both seats are always browsable so the tab bar appears
              // whenever both Collections exist, mirroring Banished.
              const localCollection = piles.find((p) => p.name === 'Collection' && !!p.rotated === localRotated);
              const oppCollection = piles.find((p) => p.name === 'Collection' && !!p.rotated !== localRotated);
              if (!localCollection) return null;
              const oppVisible = host.props.isCasualSandbox
                ? !!oppCollection
                : !!oppCollection?.revealed;
              if (!oppVisible) return null;
              const buildTab = (target, rotated) => ({
                pile: target,
                label: `${PLAYER_LABELS[rotated ? 'p2' : 'p1']} (${target.cards.length})`,
                readOnly: host.props.isSpectating
                  ? true
                  : host.props.isCasualSandbox
                    ? false
                    : (host.state.connectionStatus !== 'offline' && rotated !== localRotated),
              });
              const tabs = [buildTab(localCollection, localRotated)];
              if (oppCollection) tabs.push(buildTab(oppCollection, !localRotated));
              return tabs;
            }
            return null;
          })()}
          onSelectTab={(targetPile) => host.openPileSearch(targetPile)}
          onQueryChange={host.handlePileSearchQueryChange}
          onClose={host.handlePileSearchClose}
          onTakeToHand={(card) => {
            if (host.state.pendingMaskPick) host._resolveMaskPick(card);
            else if (host.state.pendingArchimagoBanish) host._resolveArchimagoBanishPick(card);
            else if (host.state.pendingArchimagoCopy) host._resolveArchimagoCopyPick(card);
            else if (host.state.pendingDeathspeakerPick) host._resolveDeathspeakerPick(card);
            else if (host.state.pendingDragonPick) host._resolveDragonlordSetAsidePick(card);
            else if (host.state.pendingNameSpellPick) host._resolveNameSpellPick(card);
            else if (host.state.pendingSearchEffect) host._resolveSearchPick(card);
            else host.takeCardFromPile(host.state.searchPile, card);
          }}
          onCardContextMenu={host.handlePileSearchCardContextMenu}
          onTokenSpawn={host._startManualTokenSpawn}
          pickMode={host.state.pendingMaskPick ? {
            title: t('Choose an Avatar to mask'),
            hint: t('Click an Avatar to put on the mask'),
            disableContextMenu: true,
          } : host.state.pendingDragonPick ? {
            title: t('Set aside a Unique Dragon'),
            hint: t('Click a Dragon from your collection to set it aside'),
            disableContextMenu: true,
          } : host.state.pendingNameSpellPick ? {
            title: t('Name a spell'),
            hint: t('Click a card to name it'),
            disableContextMenu: true,
          } : null}
          multiSelect={host.state.pendingPileSelect ? {
            title: host.state.pendingPileSelect.title,
            hint: host.state.pendingPileSelect.hint,
            confirmLabel: host.state.pendingPileSelect.confirmLabel,
            selectedIds: host.state.pendingPileSelect.selectedIds,
            eligibleIds: host.state.pendingPileSelect.eligibleIds,
            max: host.state.pendingPileSelect.max,
            minSelect: host.state.pendingPileSelect.minSelect ?? host.state.pendingPileSelect.max,
            onToggle: host._togglePileSelectCard,
            onConfirm: host._confirmPileSelect,
          } : null}
        />
        <ScryDialog
          scry={host.state.scry}
          onAssignCurrent={host.scryAssignCurrent}
          onPeekNext={host.scryPeekNext}
          onKeepAndDone={host.scryKeepAndDone}
          onCancel={host.scryCancel}
          onSetActive={host.scrySetActive}
          onCast={host.scryCastFromTop}
        />
        <TokenSpawnPreview
          imageUrl={host.state.pendingSummonPlacement?.imageUrl || null}
          name={host.state.pendingSummonPlacement?.tokenName}
        />
        {host.renderCardInspector()}
        {host.state.logbookMentionPreview ? (
          <CardHoverPreview
            card={host.state.logbookMentionPreview.card}
            rect={host.state.logbookMentionPreview.rect}
          />
        ) : null}
        {host.state.showFirstPlayerDice && !host.props.isSpectating ? (() => {
          const { localName, remoteName, localAvatarUrl, remoteAvatarUrl } = resolveMatchup(host);
          const isSandbox = !!host.props.isCasualSandbox;
          return (
            <FirstPlayerDiceModal
              localName={localName}
              remoteName={remoteName}
              localAvatarUrl={localAvatarUrl}
              remoteAvatarUrl={remoteAvatarUrl}
              localPlayer={host.props.arenaPlayerInfo && !isSandbox ? {
                id: host.props.arenaPlayerInfo.id,
                displayedCrownAvatarId: host.props.arenaPlayerInfo.displayedCrownAvatarId,
                isFounder: !!host.props.arenaPlayerInfo.isFounder,
                isQaTester: !!host.props.arenaPlayerInfo.isQaTester,
              } : null}
              remotePlayer={host.props.arenaOpponentInfo ? {
                id: host.props.arenaOpponentInfo.id,
                displayedCrownAvatarId: host.props.arenaOpponentInfo.displayedCrownAvatarId,
                isFounder: !!host.props.arenaOpponentInfo.isFounder,
                isQaTester: !!host.props.arenaOpponentInfo.isQaTester,
              } : null}
              sorceryCards={host.props.sorceryCards}
              isHost={host.state.isHost}
              isSandbox={isSandbox}
              localPalette={(host.state.isHost || isSandbox) ? host.state.firstPlayerRollState?.hostPalette : host.state.firstPlayerRollState?.guestPalette}
              remotePalette={(host.state.isHost || isSandbox) ? host.state.firstPlayerRollState?.guestPalette : host.state.firstPlayerRollState?.hostPalette}
              subscribeToRoll={host._subscribeToFirstPlayerRoll}
              subscribeToChoice={host._subscribeToFirstPlayerChoice}
              isAuthMatch={isAuthMatchEnabled()}
              requestServerRoll={host._requestServerRoll}
              commitFirstPlayer={host._commitFirstPlayer}
              onResult={host._resolveFirstPlayerRoll}
              onLeaveMatch={host.requestExit}
              initialRollState={host.state.firstPlayerRollState}
              onRollStateChange={host._onFirstPlayerRollStateChange}
              prediction={oracleReading.predictionProps(host)}
            />
          );
        })() : null}
        {host.state.showMatchResult ? (
          <ArenaMatchResult
            ref={(ref) => { host.matchResultRef = ref; }}
            roomCode={host.state.roomCode || null}
            matchDurationMinutes={host.state.matchStartTime ? Math.round((Date.now() - host.state.matchStartTime) / 60000) : 0}
            roundsPlayed={Math.max(host.state.turnNumbers?.p1 || 0, host.state.turnNumbers?.p2 || 0)}
            getMatchLog={() => {
              // Stamp roomCode/seat at MATCH-END. The recorder starts during
              // socket setup when host.state.roomCode can still be '' (initial
              // state), which previously produced an empty roomCode and a POST
              // to `/matches//log` (404). By match end it's reliably populated.
              if (!host.matchRecorder.isMeaningful()) return null;
              const log = host.matchRecorder.finalize();
              const roomCode = host.state.roomCode || host.props.joinRoomCode || log.roomCode;
              if (!roomCode) return null;
              log.roomCode = roomCode;
              log.seat = host.state.isHost ? 'host' : 'guest';
              return log;
            }}
            // Engine-determined winner (death-blow detected in
            // _declareGameOver). For non-arena matches we skip the
            // server report entirely. The modal shows the
            // VICTORY/DEFEAT banner + duration and closes locally.
            autoWinner={host.state.autoMatchWinner || null}
            skipRewardClaim={!host.props.isArenaMatch}
            matchup={{ ...resolveMatchup(host), solo: !!host.props.isCasualSandbox }}
            onWatchReplay={host.props.onWatchReplay || null}
            {...(() => {
              // Slot 1 is always the host's deck and slot 2 always the guest's
              // (see spawnSelectedDeck calls in initSession). Reading the local
              // player's slot ensures avatarId / deckElements credit the caller's
              // own deck. Not the opponent's. For Hall of Fame and quest stats.
              const localSlot = host.state.isHost ? 1 : 2;
              const meta = getMatchQuestMeta(host.state.spawnedDecks?.[localSlot] || null, host.props.sorceryCards);
              return { deckElement: meta.deckElement, deckElements: meta.deckElements, avatarId: meta.avatarId };
            })()}
            onSettlementSubscribe={(handler) => {
              host._matchSettledSubscriber = handler;
              if (host._pendingMatchSettlement) {
                handler(host._pendingMatchSettlement);
                host._pendingMatchSettlement = null;
              }
              return () => { if (host._matchSettledSubscriber === handler) host._matchSettledSubscriber = null; };
            }}
            onDisputeRepromptSubscribe={(handler) => {
              host._matchDisputeRepromptSubscriber = handler;
              if (host._pendingMatchDisputeReprompt) {
                handler();
                host._pendingMatchDisputeReprompt = false;
              }
              return () => { if (host._matchDisputeRepromptSubscriber === handler) host._matchDisputeRepromptSubscriber = null; };
            }}
            onRewardsApplied={(reward) => {
              // Mark the match as functionally concluded so opponent:left
              // (fired when the other seat tears down their GameBoard
              // after their own claim) doesn't render a stale "Match
              // paused. Opponent left" toast on top of the reward tally.
              host._matchConcluded = true;
              if (host.props.onMatchReward) {
                host.props.onMatchReward(reward);
              }
            }}
            onClose={() => {
              host.setState({ showMatchResult: false });
            }}
            onMatchComplete={() => {
              // Draft-tournament rounds: tell the server we've
              // dismissed the rewards modal so it can advance to
              // round N+1. Without this ACK the server waits up
              // to 90s for the fallback timer before pairing the
              // next round. Which is fine, just slow. The send
              // is fire-and-forget; a stale or missing draftMatchId
              // is a no-op on the client side.
              if (host.props.draftMatchId) {
                try { sendDraftMatchAck(host.props.draftMatchId); } catch (err) {
                  console.warn('[GameBoard] sendDraftMatchAck failed:', err);
                }
              }
              if (host.props.onExit) host.props.onExit();
            }}
          />
        ) : null}

        {host.props.isSpectating && host.state.spectatorMatchEnded ? (
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
          >
            <div
              className="flex flex-col items-center gap-4 px-10 py-8 rounded"
              style={{ ...PANEL_STYLE, border: `1px solid ${GOLD} 0.3)`, minWidth: '24cqw' }}
            >
              <div
                className="text-3xl font-bold arena-heading"
                style={{ color: ACCENT_GOLD, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.15)' }}
              >
                {t('Match Ended')}
              </div>
              <div className="text-sm" style={{ color: TEXT_MUTED }}>
                {t('The match you were watching has concluded.')}
              </div>
              <button
                type="button"
                className="mt-2 px-6 py-2 text-sm font-bold transition-all cursor-pointer"
                style={GOLD_BTN}
                data-sound={UI.CONFIRM}
                onClick={() => host.props.onExit?.()}
              >
                {t('Leave')}
              </button>
            </div>
          </div>
        ) : null}

        {/* Status-token ledge. Bottom-left, between the logbook
            (top ~16cqw) and the compendium (bottom-3). Slides
            rightward on hover; click the chevron tab to pin.
            Mutually exclusive with the compendium: when the
            compendium is open `blocked` suppresses every open
            interaction and drops the ledge below the compendium
            in the z-stack. onOpenChange feeds the parent's
            `statusTokenDrawerOpen` flag so the compendium toggle
            can refuse to open while the drawer is up. */}
        {host.props.replayDriver ? null : <StatusTokenDrawer
          tokens={host._allTokenEntries()}
          onSpawn={host.spawnTokenFromDrawer}
          blocked={host.state.showCodex}
          forceOpen={host.state.tutorialOpenStatusTokens}
          onOpenChange={(open) => host.setState({ statusTokenDrawerOpen: open })}
        />}

        {/* Streamer-facing card preview drawer. Top-right edge, just
            left of the opponent HUD column. Drops down when hovered
            or click-pinned. Shows the last face-up card the local
            player hovered, anywhere card-hover events fire (3D
            board, hand fan, logbook @-mentions). Designed so viewers
            can read the card the streamer is currently discussing. */}
        <CardPreviewDrawer
          card={host.state.focusedPreviewCard}
          onCardClick={host._openInspectorForFocusedCard}
        />

        {/* Hold Alt/Option to zoom the card under the cursor
            (TTS-style). Self-contained: subscribes to cardZoomBus,
            which is fed by mesh-hover and hand-hover above. */}
        <CardAltZoomOverlay />

        {/* Card-effect prompt. Right-edge drawer that surfaces the
            head of pendingPrompts. Confirm commits the effect to the
            ledger and broadcasts; Cancel drops the prompt without
            firing. Non-blocking. The rest of the board stays
            interactive while a prompt is showing. */}
        {/* v1.41. Storyline: pendingPrompts is now a LIFO stack
            (top = most recently pushed event, resolves first per
            codex). The EffectPromptDrawer shows the active event;
            StorylinePile renders pending events behind it as a
            receding pile. Both render nothing when the storyline
            is empty.

            draw_phase_choice events are filtered out: they own
            their own UI surface (StartDrawOverlay, pile-anchored
            buttons) and have no cardInstance, so leaking them into
            the storyline pile would render an empty text card. */}
        <StorylinePile
          pileEvents={getPileEvents(host.state.pendingPrompts)}
        />
        {/* Magics played to the storyline: card art dwells here while it
            resolves, then lands on its square via addCardToTable. */}
        <SpellCastFlightLayer casts={host.state.castingSpells} />
        <UnitHandBadges host={host} />
        <EffectPromptDrawer
          prompt={(() => {
            const active = getActiveEvent(host.state.pendingPrompts);
            if (!active) return null;
            // An informational receipt must never hide a required server choice.
            // Leave it queued and readable once that choice has resolved.
            if (active.revealNotice && host.state.pendingServerChoice) return null;
            // v1.110. When the start-phase draw is the only prompt
            // in the storyline, the pile-anchored StartDrawOverlay
            // owns its UI. Hide the drawer for that lone case so the
            // two surfaces don't render simultaneously. Any other
            // stack arrangement (draw + Seer trigger, draw behind a
            // Genesis prompt, etc.) routes the active prompt back
            // through the drawer with the standard stack styling.
            const prompts = host.state.pendingPrompts;
            if (prompts.length === 1 && active.promptType === 'draw_phase_choice') {
              return null;
            }
            return active;
          })()}
          onConfirm={host.handlePromptConfirm}
          onCancel={host.handlePromptCancel}
        />

        {/* Auth path: a server MODAL pick (Witch's Curse "choose one") renders as
            a choice drawer driven off pendingServerChoice. The button value routes
            back to the server via chooseIntent; the delegated ability skips the
            legacy prompt so the two drawers never show the same menu at once.
            A server CELL/TARGET pick renders the same drawer as a pick_cell /
            pick_target storyline entry (source card + instruction label) so the
            board halos are never unexplained; the actual pick stays a board
            click (BOARD_PICK_TYPES keeps the canvas interactive). */}
        <EffectPromptDrawer
          prompt={(() => {
            const pc = host.state.pendingServerChoice;
            if (!pc) return null;
            if (pc.kind === 'cell' || pc.kind === 'target') {
              return buildServerPickPrompt(host, pc);
            }
            if (pc.kind !== 'modal') return null;
            // Scry: resolve the server's displayed card id against the local
            // copies (own pile / mesh / hand) so the modal shows the exact
            // revealed card, orientation-independent. Shared with the
            // inspect hotkey (serverChoiceDisplayCard).
            const displayCard = serverChoiceDisplayCard(host, pc);
            // Cast-from-top Summon affordance (Cradle of Etherrum): a
            // client-appended choice; resolveServerModalChoice intercepts
            // '__cast_from_top' and emits the fromDeckTop cast intent.
            const choices = pc.castOption
              ? [...pc.choices, { label: t('Summon for ({cost})', { cost: pc.castOption.need }), value: '__cast_from_top' }]
              : pc.choices;
            return {
              id: pc.promptId,
              promptType: 'choice',
              choices,
              description: pc.description || undefined,
              cardInstance: displayCard || undefined,
              cardName: displayCard?.name || undefined,
            };
          })()}
          onConfirm={(_promptId, value) => host._resolveServerModalChoice(value)}
          onCancel={() => host._declineServerChoice()}
        />

        {/* HUD buff / life management overlays. Both panels and the
            scroll preview anchor to the clicked HUD row via a viewport
            rect that gets projected into canvas-local space. */}
        {(() => {
          const panel = host.state.hudPanel;
          const scroll = host.state.hudScroll;
          const viewerPlayer = host._viewerPlayer();
          const turnNumbers = host.state.turnNumbers;
          return (
            <>
              {panel && panel.kind === 'life' ? (
                <HudLifeAdjustPanel
                  player={panel.player}
                  viewerPlayer={viewerPlayer}
                  isCasualSandbox={host.props.isCasualSandbox}
                  isReadOnly={!host._canEditHudFor(panel.player)}
                  currentLife={host.state.gameState.trackers[panel.player]?.life ?? 0}
                  maxLife={host.props.startingHp || 20}
                  canvasLocalPos={host._canvasLocalAnchor(panel.rect)}
                  onAdjust={(delta) => host._adjustLife(panel.player, delta)}
                  onClose={host._closeHudPanel}
                />
              ) : null}
              {panel && panel.kind === 'mana' ? (
                <HudBuffPanel
                  player={panel.player}
                  viewerPlayer={viewerPlayer}
                  canEdit={host._canEditHudFor(panel.player)}
                  effectLedger={host.effectLedger}
                  currentTurn={host.state.currentTurn}
                  turnNumbers={turnNumbers}
                  sorceryCards={host.props.sorceryCards}
                  canvasLocalPos={host._canvasLocalAnchor(panel.rect)}
                  onAddBuff={({ amount, duration, n }) => host._addManualManaBuff(panel.player, amount, duration, n)}
                  onRemoveBuff={host._removeBuffEntry}
                  onClose={host._closeHudPanel}
                />
              ) : null}
              {scroll ? (
                <HudScrollAdjuster
                  kind={scroll.kind}
                  delta={scroll.delta}
                  canvasLocalPos={host._canvasLocalAnchor(scroll.rect)}
                  anchorHeight={scroll.rect?.height}
                  onCancel={host._cancelHudScroll}
                />
              ) : null}
            </>
          );
        })()}

        {/* Start-phase mandatory draw. Pile-anchored prompt.
            Replaces the side-drawer Draw a Spell / Draw a Site
            buttons with shells positioned directly over the
            Spellbook and Atlas piles. The buttons resolve the
            same draw_phase_choice prompt the drawer used to own.
            See updateStartDrawOverlays() for the per-frame
            projection. */}
        {(() => {
          const prompt = host._localDrawPhaseChoicePrompt();
          const piles = host.state.gameState?.piles || [];
          const spellPile = piles.find((p) => p.id === prompt?._spellbookPileId);
          const atlasPile = piles.find((p) => p.id === prompt?._atlasPileId);
          return (
            <StartDrawOverlay
              registerRefs={host._registerStartDrawRefs}
              spellCount={spellPile?.cards?.length ?? 0}
              atlasCount={atlasPile?.cards?.length ?? 0}
              singleDraw={!!prompt?._singleDraw}
              onChoose={(v) => prompt && host.handlePromptConfirm(prompt.id, v)}
            />
          );
        })()}

        {/* Logbook. Also shown in casual sandbox so solo play-testing
            has a scrollable history of engine actions. Chat input is
            gated separately below since sandbox has no opponent. */}
        {(host.state.connectionStatus !== 'offline' || host.props.isCasualSandbox) && (() => {
          const localName = host.props.arenaPlayerInfo?.name || 'You';
          const localAvatar = host.props.arenaPlayerInfo?.avatarUrl;
          const oppName = host.props.arenaOpponentInfo?.name || 'Opponent';
          const oppAvatar = host.props.arenaOpponentInfo?.avatarUrl;
          const logOpen = host.state.showGameLog;
          const localColor = ACCENT_GOLD;
          const oppColor = '#8ea8c8';
          // Combat prompts mount a full-canvas backdrop at 1099. Lift the
          // panel above it (and above the prompt itself) so chat stays
          // usable while a target/defend pick is pending.
          const overCombatPrompt = !!(host.state.attackTargetModal || host.state.cardActionMenu?.combatModal);
          return (
            <div
              className={`fixed left-3 ${overCombatPrompt ? 'z-[1102]' : 'z-[999]'} transition-all duration-200`}
              style={{
                top: '15.75cqw',
                width: logOpen ? '15cqw' : '2.5cqw',
                transition: 'width 0.25s ease',
              }}
            >
              <div
                className="relative overflow-hidden"
                style={{
                  // Layer order (top→bottom): noise, stone, base. Using
                  // background-blend-mode with `soft-light` on the stone
                  // lets the texture show through as a subtle gradient
                  // instead of a flat stamp, so no opaque layer is
                  // needed to calm it down and the whole panel keeps
                  // the semi-transparent glass feel.
                  backgroundImage: `url("/tex-noise.webp"), url("/tex-stone.webp")`,
                  backgroundColor: 'rgba(12, 10, 8, 0.72)',
                  backgroundBlendMode: 'normal, soft-light',
                  border: `1px solid ${GOLD} 0.2)`,
                  borderRadius: 'var(--radius-xl)',
                  // Shadows and blurs in cqw so the panel's depth
                  // scales with the canvas. At smaller canvas
                  // sizes the drop shadow stays proportional
                  // rather than looking comically heavy.
                  boxShadow: '0 0.75cqw 2cqw rgba(0,0,0,0.5), 0 0 1.25cqw rgba(180,140,60,0.04)',
                  backdropFilter: 'blur(0.625cqw)',
                  WebkitBackdropFilter: 'blur(0.625cqw)',
                  isolation: 'isolate',
                }}
              >
                <FourCorners />
                {/* Header: toggle (full width when collapsed) + copy (when expanded) */}
                <div
                  className="flex items-center w-full"
                  style={{
                    height: '2.5cqw',
                    borderBottom: logOpen ? `1px solid ${GOLD} 0.08)` : 'none',
                  }}
                >
                  <button
                    type="button"
                    className={cn('flex items-center cursor-pointer h-full justify-center', logOpen ? 'shrink-0' : 'flex-1')}
                    style={{
                      color: `${GOLD} 0.6)`,
                      padding: logOpen ? '0 0.5cqw' : 0,
                      background: 'transparent',
                      border: 'none',
                      position: 'relative',
                    }}
                    onClick={() => host.setState((s) => ({ showGameLog: !s.showGameLog }), () => {
                      if (host.state.showGameLog && host.logbookScrollRef) {
                        host.logbookScrollRef.scrollTop = host.logbookScrollRef.scrollHeight;
                        host.updateLogbookThumb();
                      }
                    })}
                    title={t('Toggle logbook')}
                  >
                    <svg style={{ width: '1.125cqw', height: '1.125cqw' }} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" className="shrink-0">
                      <path d="M3 2h8M3 5h6M3 8h4M3 11h7" />
                    </svg>
                    {/* Unread chat dot while collapsed. The tab badge is
                        hidden, so surface the notification on the toggle. */}
                    {!logOpen && host.state.chatUnread && (
                      <span className="absolute rounded-full" style={{ top: '0.5cqw', right: '0.5cqw', width: '0.4cqw', height: '0.4cqw', background: '#e8533f' }} />
                    )}
                  </button>
                  {logOpen && (() => {
                    const selectTab = (tab) => host.setState((s) => ({
                      logbookTab: tab,
                      chatUnread: isChatVisibleTab(tab) ? false : s.chatUnread,
                    }), () => {
                      saveLogbookTab(tab);
                      if (host.logbookScrollRef) {
                        host.logbookScrollRef.scrollTop = host.logbookScrollRef.scrollHeight;
                        host.updateLogbookThumb();
                      }
                    });
                    const tabButton = (tab, label) => {
                      const active = host.state.logbookTab === tab;
                      return (
                        <button
                          type="button"
                          className="arena-heading relative cursor-pointer h-full text-[0.8125cqw] tracking-wide transition-colors"
                          style={{
                            color: active ? `${GOLD} 0.95)` : `${GOLD} 0.45)`,
                            padding: '0 0.625cqw',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: active ? `2px solid ${GOLD} 0.8)` : '2px solid transparent',
                          }}
                          onClick={() => selectTab(tab)}
                        >
                          {label}
                          {tab === 'chat' && host.state.chatUnread && (
                            <span className="absolute rounded-full" style={{ top: '0.45cqw', right: '0cqw', width: '0.4cqw', height: '0.4cqw', background: '#e8533f' }} />
                          )}
                        </button>
                      );
                    };
                    return (
                      <>
                        {tabButton('log', t('Log'))}
                        {tabButton('chat', t('Chat'))}
                        {tabButton('both', t('Both'))}
                        <div className="flex-1" />
                      </>
                    );
                  })()}
                  {logOpen && host.state.logbookTab !== 'chat' && (
                    <button
                      type="button"
                      className="flex items-center justify-center cursor-pointer shrink-0 transition-colors"
                      style={{
                        width: '1.75cqw',
                        height: '100%',
                        marginRight: '0.375cqw',
                        color: `${GOLD} 0.5)`,
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; e.currentTarget.style.color = `${GOLD} 0.9)`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${GOLD} 0.5)`; }}
                      onClick={() => {
                        const lines = host.state.gameLog.map((entry) => {
                          const cleanText = stripLogCellBrackets(logEntryText(entry));
                          if (entry.actor === 'system') return `[SYSTEM] ${cleanText}`;
                          const who = entry.actor === 'you' ? localName : oppName;
                          return `${who}: ${cleanText}`;
                        });
                        const text = lines.join('\n');
                        if (!text) { toast(t('Log is empty')); return; }
                        navigator.clipboard.writeText(text)
                          .then(() => toast.success(t('Log copied to clipboard')))
                          .catch(() => toast.error(t('Failed to copy log')));
                      }}
                      title={t('Copy log to clipboard')}
                    >
                      <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="5" width="9" height="9" rx="1.5" />
                        <path d="M3 11V3a1 1 0 0 1 1-1h7" />
                      </svg>
                    </button>
                  )}
                </div>
                {/* Log entries. Native scrollbar is hidden entirely
                    (scrollbar-width: none) so content fills the full
                    width with perfectly symmetric padding. A custom
                    thumb is rendered as an absolutely-positioned
                    overlay on the right edge, sitting above the
                    content on the z-axis. That thumb's position and
                    size track the real scroll state via JS. See
                    logbookScrollRef and renderLogbookThumb(). */}
                <div
                  className="relative transition-all duration-300 ease-in-out flex flex-col"
                  style={{
                    height: logOpen ? '16.25cqw' : '0cqw',
                    opacity: logOpen ? 1 : 0,
                    overflow: 'hidden',
                  }}
                >
                <div
                  className="logbook-scroll flex flex-col flex-1 min-h-0"
                  style={{
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                  ref={host.setLogbookScrollRef}
                  onScroll={host.updateLogbookThumb}
                >
                  {(() => {
                  const tab = host.state.logbookTab;
                  const entries = logbookEntriesFor(tab, host.state.gameLog);
                  return entries.length === 0 ? (
                    <div className="text-[0.5625cqw] text-center py-8" style={{ color: TEXT_MUTED }}>{tab === 'chat' ? 'No messages yet.' : 'Awaiting first action...'}</div>
                  ) : entries.map((entry) => {
                    if (entry.actor === 'system') {
                      return (
                        <div key={entry.id} className="mx-2.5 my-1.5 px-3 py-1 rounded-md text-center text-[0.5cqw] font-semibold shrink-0" style={{ color: `${GOLD} 0.55)`, background: `${GOLD} 0.08)`, border: `1px solid ${GOLD} 0.12)` }}>
                          {logEntryText(entry)}
                        </div>
                      );
                    }
                    const isYou = entry.actor === 'you';
                    const name = isYou ? localName : oppName;
                    const avatar = isYou ? localAvatar : oppAvatar;
                    const nameColor = isYou ? localColor : oppColor;
                    const isChat = entry.kind === 'chat';
                    // v1.104. Warning rows highlight engine-flagged-but-allowed
                    // plays (illegal site placement, movement out of range,
                    // mandatory action skipped, etc.). Yellow tint so the
                    // player can scan the log and see what the engine
                    // wasn't sure about; the action itself proceeded.
                    const isWarning = entry.kind === 'warning';
                    const warnYellow = 'rgba(234, 179, 8,';
                    const rowStyle = isChat
                      ? { borderBottom: `1px solid ${GOLD} 0.04)`, background: `${GOLD} 0.07)`, borderLeft: `2px solid ${nameColor}aa`, lineHeight: 1.35 }
                      : isWarning
                      ? { borderBottom: `1px solid ${warnYellow} 0.18)`, background: `${warnYellow} 0.10)`, borderLeft: `2px solid ${warnYellow} 0.85)`, lineHeight: 1.25 }
                      : { borderBottom: `1px solid ${GOLD} 0.04)`, lineHeight: 1.25 };
                    const textStyle = isChat
                      ? { color: TEXT_PRIMARY, lineHeight: rowStyle.lineHeight, fontStyle: 'italic' }
                      : isWarning
                      ? { color: `${warnYellow} 0.95)`, lineHeight: rowStyle.lineHeight }
                      : { color: TEXT_BODY, lineHeight: rowStyle.lineHeight };
                    return (
                      <div key={entry.id} className={cn('flex items-start gap-1.5 py-1 shrink-0', isChat ? 'pl-2 pr-2.5' : 'px-2.5')} style={rowStyle}>
                        {avatar ? (
                          <img src={avatar} alt="" className="shrink-0 rounded-full object-cover" style={{ width: '0.875cqw', height: '0.875cqw', border: `1px solid ${nameColor}44` }} />
                        ) : (
                          <span className="shrink-0 rounded-full" style={{ width: '0.375cqw', height: '0.375cqw', marginTop: '0.25cqw', background: nameColor }} />
                        )}
                        <span className="text-[0.5625cqw] font-semibold shrink-0" style={{ color: nameColor, lineHeight: rowStyle.lineHeight }}>{name}</span>
                        <span className="text-[0.5625cqw] min-w-0" style={textStyle}>
                          {isChat
                            ? <CardMentionText
                                text={entry.text}
                                onOpenCard={host.handleOpenLogbookMention}
                                onCardHover={host.handleLogbookMentionHover}
                                onCardHoverEnd={host.handleLogbookMentionHoverEnd}
                              />
                            : renderLogTextWithCells(logEntryText(entry))}
                        </span>
                      </div>
                    );
                  });
                  })()}
                </div>
                <div
                  ref={(el) => { host.logbookThumbRef = el; }}
                  className="pointer-events-none absolute"
                  style={{
                    top: 0,
                    right: '0.125cqw',
                    width: '0.1875cqw',
                    height: 0,
                    borderRadius: 'var(--radius-full)',
                    background: `${GOLD} 0.45)`,
                    transition: 'opacity 150ms ease',
                    opacity: 0,
                    zIndex: 2,
                  }}
                />
                {/* Chat input. On any chat-showing tab; pinned to the bottom
                    of the fixed-height region so the panel is the same size
                    on both tabs (Log gives this space to entries instead).
                    Press Enter on the board to focus; Enter here sends and
                    blurs. Messages are ephemeral. */}
                {logOpen && isChatVisibleTab(host.state.logbookTab) && !host.props.isSpectating ? (
                  <div className="px-2 py-1.5" style={{ borderTop: `1px solid ${GOLD} 0.12)`, background: `${GOLD} 0.03)` }}>
                    <CardMentionEditor
                      singleLine
                      sorceryCards={host.props.sorceryCards}
                      value={host.state.chatInputValue}
                      maxLength={240}
                      placeholder={t('Type a message. @ to mention cards.')}
                      inputRef={(el) => { host.chatInputRef.current = el; }}
                      onValueChange={(next) => host.setState({ chatInputValue: next })}
                      onSubmit={() => {
                        host.sendChatMessage();
                        host.chatInputRef.current?.blur();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          host.chatInputRef.current?.blur();
                        }
                      }}
                      onOpenCard={host.handleOpenLogbookMention}
                      onCardHover={host.handleLogbookMentionHover}
                      onCardHoverEnd={host.handleLogbookMentionHoverEnd}
                      className="logbook-chat-input w-full px-2 py-1 rounded-md"
                      style={{
                        background: 'rgba(0,0,0,0.3)',
                        border: `1px solid ${GOLD} 0.15)`,
                        color: TEXT_PRIMARY,
                        fontFamily: 'inherit',
                        fontSize: '0.625cqw',
                        lineHeight: 1.4,
                        position: 'relative',
                      }}
                    />
                  </div>
                ) : null}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Opponent disconnected. Freeze overlay */}
        {host.state.opponentDisconnected ? (() => {
          // Arena (ranked online) drops are a win-by-default countdown: if
          // the opponent doesn't return before the grace period ends, the
          // server settles this player a full ranked win. The waiting
          // player can instead forfeit the win and void the match (no
          // ranking change for either side). Casual / draft matches keep
          // the older "paused, resume later" framing. Leaving there is a
          // pause, not a forfeit.
          const isArena = !!host.props.isArenaMatch;
          const timer = host.state.opponentDisconnectTimer;
          const mmss = timer != null ? `${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')}` : null;
          return (
          <div className="fixed inset-0 z-[1500] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}>
            <div className="relative text-center p-8" style={{ ...DIALOG_STYLE, zoom: host.state.viewScale }}>
              <FourCorners knots />
              <RuneSpinner size={48} className="mx-auto mb-4" />
              <h2 className="text-lg font-bold arena-heading mb-2" style={{ color: TEXT_PRIMARY }}>
                {t('Opponent Disconnected')}
              </h2>
              {isArena ? (
                <>
                  <p className="text-sm mb-1" style={{ color: TEXT_BODY }}>
                    {t('Waiting for them to return.')}
                  </p>
                  {mmss != null ? (
                    <p className="text-2xl font-bold tabular-nums mb-1" style={{ color: ACCENT_GOLD }}>
                      {mmss}
                    </p>
                  ) : null}
                  <p className="text-xs mb-5" style={{ color: TEXT_MUTED }}>
                    {t("You win automatically if they don't return in time. Or forfeit the win and void the match now.")}
                  </p>
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-medium cursor-pointer transition-all"
                    style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                    onClick={async () => {
                      const ok = await askConfirm({
                        title: t('Forfeit and leave?'),
                        message: t('You give up the win you would get if they do not return. The match is voided with no ranking change for either player.'),
                        confirmLabel: t('Forfeit and leave'),
                        cancelLabel: t('Keep waiting'),
                        danger: true,
                      });
                      if (!ok) return;
                      clearInterval(host._disconnectCountdown);
                      try {
                        await api.post(`/matches/${encodeURIComponent(host.state.roomCode || '')}/void`, {});
                      } catch (err) {
                        console.warn('[void] request failed:', err);
                      }
                      try { localStorage.removeItem('valkenhall-active-session'); } catch {}
                      host.setState({ opponentDisconnected: false, opponentDisconnectTimer: null });
                      await settleWithin(host.autoSave({ force: true }), LEAVE_CLEANUP_TIMEOUT_MS);
                      disconnectSocket();
                      host.props.onExit();
                    }}
                  >
                    {t('Forfeit and leave')}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm mb-1" style={{ color: TEXT_BODY }}>
                    {t('Waiting for reconnection...')}
                  </p>
                  {timer != null ? (
                    <p className="text-2xl font-bold tabular-nums mb-4" style={{ color: ACCENT_GOLD }}>
                      {timer}s
                    </p>
                  ) : null}
                  <p className="text-xs mb-5" style={{ color: TEXT_MUTED }}>
                    {t("The match is paused and saved. You can close out and resume later from the save list. Your opponent can rejoin whenever they're back.")}
                  </p>
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-medium cursor-pointer transition-all"
                    style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                    onClick={async () => {
                      clearInterval(host._disconnectCountdown);
                      try { localStorage.removeItem('valkenhall-active-session'); } catch {}
                      host.setState({ opponentDisconnected: false, opponentDisconnectTimer: null });
                      // Force-save so the blob exists even if the
                      // board's empty when the opponent left. Time-boxed
                      // so a wedged save can't trap the user on the
                      // freeze overlay (see settleWithin).
                      await settleWithin(host.autoSave({ force: true }), LEAVE_CLEANUP_TIMEOUT_MS);
                      disconnectSocket();
                      host.props.onExit();
                    }}
                  >
                    {t('Save & Leave')}
                  </button>
                </>
              )}
            </div>
          </div>
          );
        })() : null}

        {host.state.showExitConfirm ? (() => {
          const isSolo = host.props.instanceVisibility === 'solo';
          return (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
            <div className={`relative ${isSolo ? 'w-[28cqw] min-w-[26rem]' : 'w-80'} p-5`} style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.25)`, borderRadius: 'var(--radius-xl)', boxShadow: '0 0 60px rgba(0,0,0,0.5)', zoom: host.state.viewScale, isolation: 'isolate' }}>
              <FourCorners />
              <h2 className="mb-2 text-lg font-semibold arena-heading" style={{ color: TEXT_PRIMARY }}>{t('Leave game?')}</h2>
              <p className="mb-4 text-sm" style={{ color: TEXT_MUTED }}>
                {isSolo
                  ? t('Save the match to resume it later from your save list, or delete it to wipe the local save and free up the slot.')
                  : t('Your game will be saved. You can resume it later from your save list. Your opponent can rejoin when you return. Leaving is not a forfeit.')}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium cursor-pointer transition-all"
                  style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                  onClick={() => host.setState({ showExitConfirm: false })}
                >
                  {t('Cancel')}
                </button>
                {isSolo ? (
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium cursor-pointer transition-all"
                    style={{ ...BEVELED_BTN, color: '#c45050', borderRadius: 'var(--radius-md)' }}
                    onClick={async () => {
                      const instanceId = host.props.instanceId;
                      await settleWithin((async () => {
                        const [{ clearInstanceLocalBlob }, { abandonInstance }] = await Promise.all([
                          import('../../utils/game/instanceSaveAdapter'),
                          import('../../utils/arena/instancesApi'),
                        ]);
                        if (instanceId) {
                          await clearInstanceLocalBlob(instanceId).catch(() => {});
                          await abandonInstance(instanceId).catch(() => {});
                        }
                      })(), LEAVE_CLEANUP_TIMEOUT_MS);
                      host.props.onExit();
                    }}
                  >
                    {t('Delete Match')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium cursor-pointer transition-all"
                  style={{ ...BEVELED_BTN, color: TEXT_PRIMARY, borderRadius: 'var(--radius-md)' }}
                  onClick={async () => {
                    await settleWithin(host.autoSave({ force: true }), LEAVE_CLEANUP_TIMEOUT_MS);
                    host.props.onExit();
                  }}
                >
                  {t('Save & Leave')}
                </button>
              </div>
            </div>
          </div>
          );
        })() : null}
        {host.state.showSpawnConfirm ? (() => {
          const title = t('Replace current game?');
          const body = t('This will clear all cards, piles, and your hand from the table and spawn a new deck.');
          const cta = t('Replace');
          return (
            <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="w-80 rounded-2xl border border-border/70 bg-card p-5 shadow-2xl" style={{ zoom: host.state.viewScale }}>
                <h2 className="mb-2 text-lg font-semibold">{title}</h2>
                <p className="mb-4 text-sm text-muted-foreground">{body}</p>
                <div className="flex justify-end gap-2">
                  <button type="button" className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-muted" onClick={() => host.setState({ showSpawnConfirm: false })}>
                    {t('Cancel')}
                  </button>
                  <button type="button" className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700" onClick={host.confirmSpawnDeck}>
                    {cta}
                  </button>
                </div>
              </div>
            </div>
          );
        })() : null}
        {host.state.showSaveDialog ? (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => host.setState({ showSaveDialog: false })}>
            <div className="relative w-[26.25cqw] max-h-[70vh] flex flex-col" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.25)`, borderRadius: 'var(--radius-xl)', boxShadow: '0 0 60px rgba(0,0,0,0.5)', zoom: host.state.viewScale, isolation: 'isolate' }} onClick={(e) => e.stopPropagation()}>
              <FourCorners />
              <div className="p-5" style={{ borderBottom: `1px solid ${GOLD} 0.12)` }}>
                <h2 className="text-lg font-semibold arena-heading" style={{ color: TEXT_PRIMARY }}>{t('Save Session')}</h2>
              </div>

              {/* New save */}
              <div className="p-4" style={{ borderBottom: `1px solid ${GOLD} 0.12)` }}>
                <div className="text-[0.625cqw] font-semibold mb-2" style={SECTION_HEADER_STYLE}>{t('New Save')}</div>
                <div className="flex gap-2">
                  <input
                    ref={host.saveDialogInputRef}
                    type="text"
                    placeholder={t('Enter save name...')}
                    value={host.state.saveDialogName}
                    onInput={(e) => host.setState({ saveDialogName: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && host.state.saveDialogName.trim()) host.manualSave(host.state.saveDialogName); }}
                    className="flex-1 px-3 py-2 text-sm outline-none"
                    style={{ ...INPUT_STYLE, borderRadius: 'var(--radius-md)', color: TEXT_PRIMARY }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium cursor-pointer disabled:opacity-50 transition-all"
                    style={GOLD_BTN}
                    disabled={!host.state.saveDialogName.trim()}
                    onClick={() => host.manualSave(host.state.saveDialogName)}
                  >
                    {t('Save')}
                  </button>
                </div>
              </div>

              {/* Existing saves */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="text-[0.625cqw] font-semibold mb-2" style={SECTION_HEADER_STYLE}>{t('Overwrite Existing')}</div>
                {host.state.savedSessions.length === 0 ? (
                  <p className="text-xs py-2" style={{ color: TEXT_MUTED }}>{t('No existing saves.')}</p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {host.state.savedSessions.filter((s) => s.id !== 'autosave').map((session) => (
                      <button
                        key={session.id}
                        type="button"
                        className="flex items-center justify-between px-3 py-2.5 text-left text-xs cursor-pointer transition-all"
                        style={session.id === host.state.currentSessionId
                          ? { border: `1px solid ${GOLD} 0.35)`, background: `${GOLD} 0.06)`, borderRadius: 'var(--radius-md)' }
                          : { border: `1px solid ${GOLD} 0.1)`, borderRadius: 'var(--radius-md)' }
                        }
                        onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = session.id === host.state.currentSessionId ? `${GOLD} 0.06)` : 'transparent'; }}
                        onClick={() => host.manualSave(session.name, session.id)}
                      >
                        <div>
                          <div className="font-medium" style={{ color: TEXT_PRIMARY }}>{session.name}</div>
                          <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>{new Date(session.savedAt).toLocaleString()}</div>
                        </div>
                        {session.id === host.state.currentSessionId ? (
                          <span className="text-[0.5625cqw] font-semibold" style={{ color: ACCENT_GOLD }}>{t('Current')}</span>
                        ) : (
                          <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('Overwrite')}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-3 flex justify-end" style={{ borderTop: `1px solid ${GOLD} 0.12)` }}>
                <button type="button" className="px-4 py-2 text-sm cursor-pointer transition-all" style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }} onClick={() => host.setState({ showSaveDialog: false })}>
                  {t('Cancel')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Hand */}
      {host.renderHand(handCards, 'center')}
      {false && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[1000]"
          style={{
            height: host.state.handRetracted ? '60px' : '140px',
            transition: 'height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
          onMouseEnter={() => {
            clearTimeout(host.handRetractTimer);
            host.setState({ handRetracted: false });
          }}
          onMouseMove={(e) => {
            clearTimeout(host.handRetractTimer);
            const N = handCards.length;
            if (N === 0) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - rect.left - rect.width / 2;
            const maxAnglePerCard = N <= 3 ? 3 : N <= 5 ? 3 : N <= 8 ? 3 : N <= 12 ? 2 : 1.2;
            const totalArcMax = N <= 5 ? 15 : N <= 8 ? 22 : N <= 12 ? 22 : 15;
            const anglePerCard = Math.min(maxAnglePerCard, totalArcMax / Math.max(N - 1, 1));
            const arcRadius = 1600;
            const totalArc = anglePerCard * (N - 1);

            let closestIdx = -1;
            let closestDist = Infinity;
            for (let i = 0; i < N; i++) {
              const angleDeg = N === 1 ? 0 : -totalArc / 2 + i * anglePerCard;
              const cardX = arcRadius * Math.sin(angleDeg * Math.PI / 180);
              const dist = Math.abs(mouseX - cardX);
              if (dist < closestDist && dist < 80) {
                closestDist = dist;
                closestIdx = i;
              }
            }

            if (closestIdx !== host.state.hoveredHandIndex) {
              host.setState({ hoveredHandIndex: closestIdx });
            }
          }}
          onMouseLeave={() => {
            host.setState({ hoveredHandIndex: -1 });
            host.handRetractTimer = setTimeout(() => {
              host.setState({ handRetracted: true });
            }, 2000);
          }}
        >
          {(() => {
            // Resolve the mulligan flags for the player whose hand is
            // being shown. In multiplayer this is always the LOCAL
            // player's set (unsuffixed); in casual sandbox it follows
            // Tab (the active P1/P2 side).
            const flagsKey = host._mulliganKeys(host._localPlayerNum()).flags;
            const flagged = host.state[flagsKey] || new Set();
            // v1.73. Playable-card highlight. _canPlayFromHand
            // returns ok=true only when it's the controller's turn,
            // the mandatory-site gate (if any) is satisfied, AND
            // affordability passes. Cheap per-card call. Short-
            // circuits at the turn check for the opponent's hand
            // (rendered when sandbox flips active seat).
            const playableIds = new Set();
            if (host._automationsEnabled()) {
              for (const c of handCards) {
                if (c.faceDown) continue;
                if (host._canPlayFromHand(c)?.ok) playableIds.add(c.id);
              }
            }
            return handCards.map((card, i) => {
            const N = handCards.length;
            // Dynamic arc: less curve with more cards so edges stay visible
            const maxArc = N <= 5 ? 30 : N <= 8 ? 25 : N <= 12 ? 18 : 12;
            const maxAnglePerCard = N <= 5 ? 6 : N <= 8 ? 3.5 : N <= 12 ? 2 : 1.2;
            const anglePerCard = Math.min(maxAnglePerCard, maxArc / Math.max(N - 1, 1));
            const totalArc = anglePerCard * (N - 1);
            const angleDeg = N === 1 ? 0 : -totalArc / 2 + i * anglePerCard;
            const arcRadius = N <= 8 ? 1600 : 2400;

            const angleRad = angleDeg * (Math.PI / 180);
            let x = arcRadius * Math.sin(angleRad);
            const retractOffset = host.state.handRetracted ? 90 : 0;
            let y = arcRadius - arcRadius * Math.cos(angleRad) + 50 + retractOffset;
            let rotation = angleDeg;
            let scale = 1;
            let zIndex = i + 1;

            const hovered = host.state.hoveredHandIndex;
            if (i === hovered) {
              y = card.isSite ? -10 : -5;
              rotation = 0;
              scale = 1.8; // hover pop (matches the main hand fan's hoverScale)
              zIndex = N + 10;
            } else if (hovered >= 0) {
              const distance = Math.abs(i - hovered);
              const direction = i < hovered ? -1 : 1;
              x += direction * (60 / (distance + 0.5));
            }

            return (
              <div
                key={card.id}
                className="absolute pointer-events-auto cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => { if (e.button === 0) host.beginHandDrag(e, card, 'center'); }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  host.setState({
                    contextMenu: { x: e.clientX, y: e.clientY, type: 'handcard', cardInstance: card },
                  });
                }}
                style={{
                  left: '50%',
                  bottom: '0px',
                  width: '120px',
                  height: '175px',
                  zIndex,
                  transformOrigin: 'bottom center',
                  transition: 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.18s, filter 0.18s',
                  transform: `translateX(calc(-50% + ${x}px)) translateY(${y - (card.isSite ? 30 : 0)}px) rotate(${rotation + (card.isSite ? 90 : 0)}deg) scale(${scale})`,
                  // Warm-gold mulligan selection glow. Mirrors the
                  // emissive 0x5a3f0a tint used on selected table-
                  // card meshes (GameBoard.jsx ~1821). Applied to
                  // the OUTER wrapper because the inner div carries
                  // a CSS `mask` via .card-mask which clips every
                  // box-shadow (inset or outer) to zero. Inset ring
                  // reads as an outline on the rounded card edge;
                  // outer glow sells the "selected" affordance.
                  borderRadius: RADIUS.card,
                  // v1.73. Three-state halo layering. Mulligan flag
                  // (gold) wins when set; otherwise playable cards
                  // get a soft green glow so the player can see at
                  // a glance what they can cast / place right now.
                  // Sites highlight under the same rule. Mandatory-
                  // site exception is already baked into _canPlayFromHand.
                  boxShadow: flagged.has(card.id)
                    ? '0 0 0 3px rgba(212, 168, 67, 0.95), 0 0 26px rgba(212, 168, 67, 0.6)'
                    : card._castableOnlyBy
                      ? '0 0 0 2px rgba(178, 140, 230, 0.9), 0 0 22px rgba(178, 140, 230, 0.55)'
                      : playableIds.has(card.id)
                        ? '0 0 0 2px rgba(140, 220, 130, 0.85), 0 0 22px rgba(140, 220, 130, 0.5)'
                        : undefined,
                  filter: flagged.has(card.id)
                    ? 'brightness(1.08) saturate(1.05)'
                    : playableIds.has(card.id)
                      ? 'brightness(1.05)'
                      : 'none',
                }}
              >
                <div
                  className={cn(
                    'w-full h-full overflow-hidden rounded-card card-mask',
                    isFoilFinish(card.foiling) && FOIL_OVERLAY_CLASSES
                  )}
                  data-foil={isFoilFinish(card.foiling) ? card.foiling : undefined}
                >
                  <img
                    src={
                      card.faceDown
                        ? `${getLocalApiOrigin()}/game-assets/${card.isSite ? 'cardback-atlas-rounded.png' : 'cardback-spellbook-rounded.png'}`
                        : resolveLocalImageUrl(card.imageUrl)
                    }
                    alt={card.faceDown ? t('Face-down card') : card.name}
                    className="w-full h-full object-cover shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
                    draggable={false}
                  />
                </div>
                {card.revealed ? <HandRevealBadge /> : null}
                {card._castableOnlyBy ? <HandCastLockBadge /> : null}
              </div>
            );
            });
          })()}
        </div>
      )}

      {/* Controller overlays. Reticle + radial menu. Only render
          when the most recent input source is a gamepad so mouse
          players never see them. */}
      {showControllerOverlay ? (
        <ControllerAimReticle
          x={controllerAim.x}
          y={controllerAim.y}
          snapped={Boolean(host.hoveredMesh)}
          visible={!host.controllerRadialOpen}
        />
      ) : null}
      {/* Ping reticle. Follows the mouse while the ping key is held.
          Matches the in-world ring color so the cursor previews what
          you'll fire. */}
      {host.state.pingMode ? (
        <PingReticle color="#ffd24a" />
      ) : null}
      {showControllerOverlay && host.controllerRadialOpen ? (
        <RadialActionMenu
          visible
          centerX={controllerAim.x}
          centerY={controllerAim.y}
          selectedIndex={host.controllerRadialIndex}
        />
      ) : null}
    </div>
  );
}

export function renderHand(host, cards, position) {
  const isLeftEarly = position === 'left';
  // Scale the hand off the live .app-canvas scale so its cards + arc
  // grow with the board (and the rest of the cqw UI) at every window
  // size, uncapped, instead of the old window/1920 factor that capped
  // at 2.5 and left the hand undersized on large displays.
  const s = host.state.canvasScale;

  // When the hand is empty we still render an invisible drop zone
  // (only the center hand, not the left atlas variant) so players
  // can drag a table card back into an empty hand. Without this,
  // isPointOverHand can't find the container and the handoff fails.
  // pointer-events: none keeps the zone from intercepting anything.
  if (!cards || cards.length === 0) {
    if (isLeftEarly) return null;
    const emptyZoneWidth = Math.round(600 * s);
    const emptyZoneHeight = Math.round(120 * s);
    return (
      <div
        data-tutorial="board-hand"
        className="fixed z-[1000] pointer-events-none"
        style={{
          bottom: '0px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${emptyZoneWidth}px`,
          height: `${emptyZoneHeight}px`,
        }}
      />
    );
  }
  const cardW = Math.round(160 * s);
  const cardH = Math.round(224 * s);
  const arcRadius = Math.round(1800 * s);
  const spreadBase = Math.round(70 * s);
  const hoverDetectRadius = Math.round(100 * s);
  const containerHeight = Math.round(190 * s);
  const retractedHeight = Math.round(70 * s);
  const retractOffset = Math.round(120 * s);
  const hoverScale = 1.8;

  // Compute how wide the fan of cards actually is so the container only
  // covers that region. Everything outside the fan passes through to the
  // canvas beneath. No more full-width blocking div.
  const N = cards.length;

  const isLeft = position === 'left';
  const hoveredKey = isLeft ? 'hoveredAtlasHandIndex' : 'hoveredHandIndex';
  const retractKey = isLeft ? 'atlasHandRetracted' : 'handRetracted';

  // Mulligan flags for whichever player's hand is currently displayed.
  // In multiplayer this is always the LOCAL player's set (unsuffixed);
  // in casual sandbox it follows activePlayerNum.
  const mulliganFlagsKey = host._mulliganKeys(host._localPlayerNum()).flags;
  const mulliganFlagged = host.state[mulliganFlagsKey] || new Set();

  // v1.111. Three mutually-exclusive aura sets for hand cards.
  // Priority: purple > gold > green.
  //   purple (harbinger) → Minion that Harbinger's marked-cell discount
  //                        applies to AND is castable when the discount
  //                        is taken into account. Includes Minions that
  //                        could be played for free / cheaper on a mark
  //                        even if they were already affordable.
  //   gold   (templar)   → Card with a non-cell discount applying
  //                        (Templar, Tax Collector). Player must be able
  //                        to cast at the non-cell-discounted price.
  //   green  (playable)  → Card the player can cast at full cost or via
  //                        a non-cell discount. Covers everything else
  //                        not flagged purple/gold.
  //
  // `baseOk` is the unified "is this castable, ignoring cell" gate.
  // turn match + mandatory action + best-case affordability. The
  // best-case includes Harbinger's discount, so a Minion only
  // playable via Harbinger still surfaces here for the purple check.
  const localController = host._localPlayerNum() === 2 ? 'p2' : 'p1';
  const playableIds = new Set();
  const discountedIds = new Set();
  const harbingerOnlyIds = new Set();
  // Dragon Minions playable ONLY thanks to a dragon-portal cell
  // discount (Dragonlord's Lair / Caelestis). Same cell-restricted
  // mechanic as Harbinger, but gets the teal portal aura instead of
  // purple so the hand glow matches the portal colour.
  const dragonPortalOnlyIds = new Set();
  const townOnlyIds = new Set();
  // v1.115. Avatar of Fire ignite: red glow + click-to-cast on every
  // Fire Site in the controller's hand while the ignite flag is
  // active. Wins priority over the green / gold / purple auras for
  // those cards so the player can see at a glance which cards are
  // currently castable as Fireballs.
  const fireIgniteIds = new Set();
  const igniteActive = host._isAvatarOfFireIgniteActive(localController);
  // Pending discard-cost pick (Ignis Rex): red glow + click-to-discard
  // on every hand card matching the cost filter. Same visual treatment
  // as the fire-ignite pick. It is the active "choose a hand card"
  // interaction and wins priority over the green / gold / purple auras.
  const discardPickIds = new Set();
  const pendingDiscard = host._pendingDiscardCost;
  const discardActive = !!pendingDiscard && pendingDiscard.controller === localController;
  // A server hand prompt is a modal hand interaction: narrowing the glow to
  // the candidate set (qa#81) must NOT re-enable dragging the un-glowed cards
  // mid-prompt (playing a Site while the server waits on a mandatory discard).
  // Every hand click routes to the pick resolver while one is open; an
  // ineligible pick toasts there. Local costs keep their filter-scoped lock.
  const serverDiscardLock = discardActive && pendingDiscard.kind === 'server_choice';
  // Animist armed "next magic → Spirit": every Magic in hand gets a
  // spectral violet aura. The player drags one to a realm cell to summon
  // it as a Spirit; the arm flag drives the type flip at the boundary.
  const animistSpiritIds = new Set();
  const animistArmed = !!host._animistSpiritArm?.[localController];
  // The Apex of Babel free-cast window: every spell in hand is castable
  // for free, so it glows playable regardless of mana.
  const freeCastActive = host._isFreeCastActive(localController);
  const playerResources = host.playerResources.snapshot(localController);
  for (const c of cards) {
    if (c.faceDown) continue;
    const fc = host._getFullCard(c.cardId);
    if (!fc) continue;
    if (igniteActive && isFireSite(c, host.props.sorceryCards || [])) {
      fireIgniteIds.add(c.id);
    }
    if (discardActive && isEligibleDiscardPick(pendingDiscard, c, fc)) {
      discardPickIds.add(c.id);
    }
    if (animistArmed && (fc.type ?? fc.guardian?.type) === 'Magic') {
      animistSpiritIds.add(c.id);
    }
    const baseOk = host._canPlayFromHand(c)?.ok;
    if (!baseOk) continue;
    const ty = fc.type ?? fc.guardian?.type;
    if (ty === 'Site') {
      playableIds.add(c.id);
      continue;
    }
    if (freeCastActive) {
      // Cost is waived. Any spell is playable. Skip the affordability
      // gate so unaffordable spells still glow during the window.
      playableIds.add(c.id);
      continue;
    }
    const nonCellDelta = host.effectLedger.nonCellDiscountTotal(localController, fc);
    const cellDelta = host.effectLedger.cellRestrictedDiscountTotal(localController, fc, null);
    // Purple takes priority for Minions whenever Harbinger's discount
    // would actually fire on this card (cellDelta < 0 means the entry
    // is present, has uses left, and the filter type matches Minion).
    if (ty === 'Minion' && cellDelta < 0) {
      const discountKind = host.effectLedger.cellRestrictedDiscountKind(localController, fc);
      if (discountKind === 'town') {
        townOnlyIds.add(c.id);
      } else if (discountKind === 'dragon') {
        dragonPortalOnlyIds.add(c.id);
      } else {
        harbingerOnlyIds.add(c.id);
      }
      continue;
    }
    const nonCellAfford = getAffordability(fc, playerResources, { costAdjustment: nonCellDelta });
    if (!nonCellAfford.ok) continue; // unaffordable without Harbinger; no aura.
    playableIds.add(c.id);
    if (nonCellDelta < 0) discountedIds.add(c.id);
  }

  // Use existing state keys for spellbook hand, simple hover for atlas.
  // While a hand-drag is in progress treat the dragged card as
  // "hovered" so it stays in the elevated/scaled state and neighbours
  // keep spreading out of the way as the user reorders.
  const hoveredRaw = isLeft ? (host.state.hoveredAtlasHandIndex ?? -1) : host.state.hoveredHandIndex;
  const dragIdx = !isLeft ? host.state.handDragIndex : -1;
  const hovered = dragIdx >= 0 ? dragIdx : hoveredRaw;
  const retracted = isLeft ? (host.state.atlasHandRetracted !== false) : host.state.handRetracted;

  // For the center hand: shrink the container to just the card fan
  // width (plus generous padding for hover-expansion) instead of the
  // full viewport width. This lets clicks in the empty regions on either
  // side pass through to the canvas beneath.
  let fanContainerWidth;
  if (!isLeft) {
    const maxAPC = N <= 3 ? 3 : N <= 5 ? 3 : N <= 8 ? 3 : N <= 12 ? 2 : 1.2;
    const tArcMax = N <= 5 ? 15 : N <= 8 ? 22 : N <= 12 ? 22 : 15;
    const apc = Math.min(maxAPC, tArcMax / Math.max(N - 1, 1));
    const tArc = apc * (N - 1);
    const halfArcRad = (tArc / 2) * (Math.PI / 180);
    const fanSpan = N <= 1 ? 0 : 2 * arcRadius * Math.sin(halfArcRad);
    // Padding: full hovered card width on each side + spread room
    fanContainerWidth = Math.round(fanSpan + cardW * hoverScale + spreadBase * 4);
  }

  return (
    <div
      data-tutorial={isLeft ? 'board-atlas-hand' : 'board-hand'}
      className="fixed z-[1000]"
      style={isLeft ? {
        left: retracted ? '-120px' : '0px', bottom: '60px', width: '200px', height: 'auto',
        transition: 'left 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        overflow: 'visible',
      } : {
        bottom: '0px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: `${fanContainerWidth}px`,
        maxWidth: '100vw',
        height: retracted ? `${retractedHeight}px` : `${containerHeight}px`,
        transition: 'height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}
      onMouseEnter={() => {
        clearTimeout(isLeft ? host.atlasRetractTimer : host.handRetractTimer);
        host.setState({ [retractKey]: false });
      }}
      onMouseMove={(e) => {
        perf.count('hand.mousemove');
        clearTimeout(isLeft ? host.atlasRetractTimer : host.handRetractTimer);

        // Skip hover detection while a hand card is being dragged
        if (host.state.handDragIndex >= 0) return;

        if (isLeft) {
          // Vertical stack for atlas
          const rect = e.currentTarget.getBoundingClientRect();
          const mouseY = e.clientY - rect.top;
          const cardHeight = 70;
          const idx = Math.floor(mouseY / cardHeight);
          const clampedIdx = Math.max(-1, Math.min(idx, cards.length - 1));
          if (clampedIdx !== hovered) {
            if (clampedIdx >= 0) try { playUI(UI.HOVER, { volume: 0.4 }); } catch {}
            host.setState({ [hoveredKey]: clampedIdx });
          }
        } else {
          // Arc for spellbook
          const rect = e.currentTarget.getBoundingClientRect();
          const mouseX = e.clientX - rect.left - rect.width / 2;
          const N = cards.length;
          const maxAnglePerCard = N <= 3 ? 3 : N <= 5 ? 3 : N <= 8 ? 3 : N <= 12 ? 2 : 1.2;
          const totalArcMax = N <= 5 ? 15 : N <= 8 ? 22 : N <= 12 ? 22 : 15;
          const hoverAnglePerCard = Math.min(maxAnglePerCard, totalArcMax / Math.max(N - 1, 1));
          const totalArc = hoverAnglePerCard * (N - 1);

          let closestIdx = -1;
          let closestDist = Infinity;
          for (let i = 0; i < N; i++) {
            const angleDeg = N === 1 ? 0 : -totalArc / 2 + i * hoverAnglePerCard;
            const cardX = arcRadius * Math.sin(angleDeg * Math.PI / 180);
            const dist = Math.abs(mouseX - cardX);
            if (dist < closestDist && dist < hoverDetectRadius) { closestDist = dist; closestIdx = i; }
          }
          if (closestIdx !== hovered) {
            if (closestIdx >= 0) try { playUI(UI.HOVER, { volume: 0.4 }); } catch {}
            host.setState({ [hoveredKey]: closestIdx });
          }
        }
      }}
      onMouseLeave={() => {
        if (host.state.handDragIndex >= 0) return;
        host.setState({ [hoveredKey]: -1 });
        const timer = setTimeout(() => host.setState({ [retractKey]: true }), 2000);
        if (isLeft) host.atlasRetractTimer = timer; else host.handRetractTimer = timer;
      }}
    >
      {cards.map((card, i) => {
        const N = cards.length;

        if (isLeft) {
          // Vertical stack on the left
          let yPos = i * 65;
          let scale = 1;
          let zIndex = i + 1;

          if (i === hovered) {
            scale = hoverScale;
            zIndex = N + 10;
          }

          const isPlayable = playableIds.has(card.id);
          const isFireIgnite = fireIgniteIds.has(card.id);
          const isDiscardPick = discardPickIds.has(card.id);
          return (
            <div
              key={card.id}
              className={cn(
                'absolute pointer-events-auto',
                (isFireIgnite || isDiscardPick) ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
              )}
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                if (isDiscardPick || serverDiscardLock) {
                  e.preventDefault();
                  e.stopPropagation();
                  host._resolveDiscardCostPick(card);
                  return;
                }
                if (isFireIgnite) {
                  e.preventDefault();
                  e.stopPropagation();
                  host._castFireSiteAsFireball(card);
                  return;
                }
                host.beginHandDrag(e, card, 'left');
              }}
              onContextMenu={(e) => {
                e.preventDefault(); e.stopPropagation();
                host.setState({ contextMenu: { x: e.clientX, y: e.clientY, type: 'handcard', cardInstance: card } });
              }}
              style={{
                left: '10px',
                top: `${yPos}px`,
                width: '120px',
                height: '82px',
                zIndex,
                transformOrigin: 'left center',
                transition: 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.18s, filter 0.18s',
                transform: `scale(${scale})`,
                borderRadius: RADIUS.cardLandscape,
                boxShadow: (isFireIgnite || isDiscardPick)
                  ? '0 0 0 3px rgba(232, 86, 64, 0.95), 0 0 22px rgba(232, 86, 64, 0.6)'
                  : isPlayable
                    ? '0 0 0 2px rgba(140, 220, 130, 0.85), 0 0 22px rgba(140, 220, 130, 0.5)'
                    : undefined,
                filter: (isFireIgnite || isDiscardPick)
                  ? 'brightness(1.1) saturate(1.2)'
                  : isPlayable ? 'brightness(1.05)' : undefined,
              }}
            >
              <HandCardFaces
                card={card}
                faceClassName="w-full h-full overflow-hidden rounded-card-landscape card-mask card-mask--landscape"
              />
              {card.revealed ? <HandRevealBadge /> : null}
            </div>
          );
        }

        // Spellbook hand. Fanned arc at bottom center
        const maxAnglePC = N <= 3 ? 3 : N <= 5 ? 3 : N <= 8 ? 3 : N <= 12 ? 2 : 1.2;
        const totalArcMax = N <= 5 ? 15 : N <= 8 ? 22 : N <= 12 ? 22 : 15;
        const anglePerCard = Math.min(maxAnglePC, totalArcMax / Math.max(N - 1, 1));
        const totalArc = anglePerCard * (N - 1);
        const angleDeg = N === 1 ? 0 : -totalArc / 2 + i * anglePerCard;

        const angleRad = angleDeg * (Math.PI / 180);
        let x = arcRadius * Math.sin(angleRad);
        const rOffset = retracted ? retractOffset : 0;
        let y = arcRadius - arcRadius * Math.cos(angleRad) + 50 * s + rOffset;
        let rotation = angleDeg;
        let scale = 1;
        let zIndex = i + 1;

        if (i === hovered) {
          scale = 1.5;
          y = Math.round(-5 * s);
          zIndex = N + 10;
          if (card.isSite) {
            // Rotating 90° around bottom-center displaces the visual
            // center right by cardH*scale/2 and down by cardW*scale/2.
            // Compensate both so the card is centered on the cursor
            // and fully visible above the screen edge.
            rotation = 90;
            x -= Math.round(cardH * scale / 2);
            y -= Math.round(cardW * scale / 2);
          } else {
            rotation = 0;
          }
        } else if (hovered >= 0) {
          const distance = Math.abs(i - hovered);
          const direction = i < hovered ? -1 : 1;
          x += direction * (spreadBase / (distance + 0.5));
        }

        const isMulliganFlagged = mulliganFlagged.has(card.id);
        const isPlayable = playableIds.has(card.id);
        const isDiscounted = discountedIds.has(card.id);
        const isHarbingerOnly = harbingerOnlyIds.has(card.id);
        const isDragonPortalOnly = dragonPortalOnlyIds.has(card.id);
        const isTownOnly = townOnlyIds.has(card.id);
        const isFireIgnite = fireIgniteIds.has(card.id) || discardPickIds.has(card.id);
        const isDiscardPick = discardPickIds.has(card.id);
        const isAnimistSpirit = animistSpiritIds.has(card.id);
        return (
          <div
            key={card.id}
            data-hand-card={card.id}
            aria-label={card.name}
            className={cn(
              'absolute pointer-events-auto',
              isFireIgnite ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
              // Mutually exclusive: Harbinger-only purple / dragon-portal
              // teal win over the gold discount glow, since the card isn't
              // actually playable with the non-cell discount alone. The
              // teal mirrors the dragon portal so Dragonlord's Lair reads
              // as one effect across the board and the hand.
              isTownOnly && !isMulliganFlagged && !isFireIgnite ? 'vh-hand-card-town-glow' : null,
              isHarbingerOnly && !isMulliganFlagged && !isFireIgnite ? 'vh-hand-card-harbinger-glow' : null,
              isDragonPortalOnly && !isMulliganFlagged && !isFireIgnite ? 'vh-hand-card-dragon-glow' : null,
              isDiscounted && !isHarbingerOnly && !isDragonPortalOnly && !isMulliganFlagged && !isFireIgnite ? 'vh-hand-card-discount-glow' : null,
            )}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              if (isDiscardPick || serverDiscardLock) {
                e.preventDefault();
                e.stopPropagation();
                host._resolveDiscardCostPick(card);
                return;
              }
              if (isFireIgnite) {
                e.preventDefault();
                e.stopPropagation();
                host._castFireSiteAsFireball(card);
                return;
              }
              host.beginHandDrag(e, card, 'center');
            }}
            onContextMenu={(e) => {
              e.preventDefault(); e.stopPropagation();
              host.setState({ contextMenu: { x: e.clientX, y: e.clientY, type: 'handcard', cardInstance: card } });
            }}
            style={{
              left: '50%',
              bottom: '0px',
              width: `${cardW}px`,
              height: `${cardH}px`,
              zIndex,
              transformOrigin: 'bottom center',
              transition: 'transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.18s, filter 0.18s',
              transform: `translateX(calc(-50% + ${x}px)) translateY(${y}px) rotate(${rotation}deg) scale(${scale})`,
              // Warm-gold mulligan selection glow on the outer wrapper.
              // The inner div carries a CSS `mask` (card-mask) that
              // clips every box-shadow to zero, so the glow has to
              // live out here where nothing masks it away. v1.73.
              // mulligan flag (gold) wins when set; otherwise playable
              // cards get a soft green halo so the player can see at
              // a glance what they can cast / place right now. v1.112
              // When a cost modifier WILL apply (Templar's first
              // Knight/Sir/Dame, Harbinger's marked-cell minion), the
              // .vh-hand-card-discount-glow class layers a pulsing
              // amber animation on TOP of the inline green. CSS
              // animations override inline styles only while they're
              // active, so the moment the discount is consumed and the
              // class drops, the inline playable-green is already
              // there as the underlay. No fallback-flicker, and the
              // remaining Knight stays green for as long as it's
              // affordable at full price.
              borderRadius: RADIUS.card,
              boxShadow: isFireIgnite
                ? '0 0 0 3px rgba(232, 86, 64, 0.95), 0 0 26px rgba(232, 86, 64, 0.6)'
                : isAnimistSpirit
                  ? '0 0 0 3px rgba(178, 140, 230, 0.95), 0 0 28px rgba(178, 140, 230, 0.65)'
                  : isMulliganFlagged
                    ? '0 0 0 3px rgba(212, 168, 67, 0.95), 0 0 26px rgba(212, 168, 67, 0.6)'
                    : isPlayable
                      ? '0 0 0 2px rgba(140, 220, 130, 0.85), 0 0 22px rgba(140, 220, 130, 0.5)'
                      : undefined,
              filter: isFireIgnite
                ? 'brightness(1.1) saturate(1.2)'
                : isAnimistSpirit
                  ? 'brightness(1.12) saturate(1.2)'
                  : isMulliganFlagged
                    ? 'brightness(1.08) saturate(1.05)'
                    : isDiscounted
                      ? 'brightness(1.1) saturate(1.15)'
                      : isPlayable
                        ? 'brightness(1.05)'
                        : undefined,
            }}
          >
            <HandCardFaces
              card={card}
              faceClassName="w-full h-full overflow-hidden rounded-card card-mask"
            />
            {card.revealed ? <HandRevealBadge /> : null}
          </div>
        );
      })}
    </div>
  );
}

// Hand card renderer with a CSS 3D flip between front (card face) and
// back (card back art). Mirrors the on-table flip feel: toggling
// card.faceDown tweens rotateY between 0° and 180° rather than
// snapping the image. Foil overlay + shadow stay pinned to the front
// face; the back is the plain card back art (atlas back for sites,
// spellbook back for everything else).
//
// Atlas caveat: cardback-atlas-rounded.png is authored landscape
// (wider than tall) because sites sit tapped on the table, where a
// 90° mesh rotation makes the landscape back fit the portrait card
// geometry. In the hand the container stays portrait, so we have to
// do the same 90° rotation ourselves on the back image. Otherwise
// the atlas pattern shows up sideways, cropped to portrait.
// Hand card art with an error-retry loop. The hand <img>s have stable
// keys, so a single failed load (network blip, cache race on the web
// build) would otherwise stay a blank card for as long as it sits in
// hand. Retry with a URL nonce (forces a re-request past a cached
// error) a few times before giving up to the alt text.
const HAND_IMG_MAX_RETRIES = 5;
const HAND_IMG_RETRY_DELAY_MS = 600;

class HandCardImg extends Component {
  state = { attempt: 0 };

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src) {
      clearTimeout(this._retryTimer);
      this.setState({ attempt: 0 });
    }
  }

  componentWillUnmount() {
    clearTimeout(this._retryTimer);
  }

  handleError = () => {
    if (this.state.attempt >= HAND_IMG_MAX_RETRIES) return;
    this._retryTimer = setTimeout(() => {
      this.setState((s) => ({ attempt: s.attempt + 1 }));
    }, HAND_IMG_RETRY_DELAY_MS);
  };

  render() {
    const { src, ...rest } = this.props;
    const { attempt } = this.state;
    const url = !src || attempt === 0
      ? src
      : `${src}${src.includes('?') ? '&' : '?'}_retry=${attempt}`;
    return <img src={url} decoding="async" onError={this.handleError} {...rest} />;
  }
}

function HandCardFaces({ card, faceClassName }) {
  const backSrc = `${getLocalApiOrigin()}/game-assets/${card.isSite ? 'cardback-atlas-rounded.png' : 'cardback-spellbook-rounded.png'}`;
  const foiling = isFoilFinish(card.foiling) ? card.foiling : null;
  // Rotate the atlas back 90° to match the site's visual orientation
  // when flipped face-down in hand. The atlas back PNG is authored
  // landscape (7:5), and the hand slot is portrait (5:7). Sizing the
  // image at width=140% / height=auto gives it the container's
  // swapped dimensions. 140% × (5/7 * 140%) ≈ 140% × 100% relative
  // to the portrait box. So after rotating 90° it fits exactly. No
  // object-cover cropping, no scale guesswork, no sideways atlas
  // pattern.
  const backImgStyle = card.isSite
    ? {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '140%',
        height: 'auto',
        maxWidth: 'none',
        transform: 'translate(-50%, -50%) rotate(90deg)',
      }
    : { width: '100%', height: '100%', objectFit: 'cover' };
  return (
    <div
      className="w-full h-full relative"
      style={{
        transformStyle: 'preserve-3d',
        transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: card.faceDown ? 'perspective(1000px) rotateY(180deg)' : 'perspective(1000px) rotateY(0deg)',
      }}
    >
      <div
        className={cn('absolute inset-0', faceClassName)}
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
      >
        <HandCardImg
          src={resolveLocalImageUrl(card.imageUrl)}
          alt={card.name}
          className="w-full h-full object-cover shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
          draggable={false}
        />
        {/* Foil: a per-card FoilCanvas mounted as a child of the front face, so
            it rides the card's EXACT CSS transform (fan, flip, hover lift, drag
            reorder) on the compositor. No main-thread read-lag / ghosting, and
            it lifts in z with the hovered card. Bounded by FOIL_CANVAS_MAX. */}
        {foiling && !card.faceDown ? (
          <FoilCanvas
            imageUrl={resolveLocalImageUrl(card.imageUrl)}
            zones={foilZonesForCard(card)}
            params={DEFAULT_FOIL_PARAMS}
            scrollSweep={false}
          />
        ) : null}
      </div>
      <div
        className={cn('absolute inset-0', faceClassName)}
        style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
        }}
      >
        <img
          src={backSrc}
          alt={t('Face-down card')}
          className="shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
          draggable={false}
          style={backImgStyle}
        />
      </div>
    </div>
  );
}

// Eye icon overlay rendered on a hand card that's been revealed to the
// opponent. Same role as MTGA's "visible to opponent" glyph: a constant
// reminder that this card is public info until the player explicitly
// hides it again.
function MulliganFlagBadge() {
  // Red warning X that sits in the corner of a hand card flagged for
  // the mulligan return. Uses danger red instead of the gold accent so
  // it's instantly distinguishable from the reveal-to-opponent eye icon.
  return (
    <div
      className="absolute top-1 right-1 flex items-center justify-center rounded-full pointer-events-none"
      style={{
        width: 22,
        height: 22,
        background: 'rgba(120, 20, 20, 0.92)',
        border: '1.5px solid #e66a6a',
        boxShadow: '0 0 8px rgba(0,0,0,0.6), 0 0 10px rgba(230,106,106,0.45)',
        color: '#ffe0e0',
      }}
      title={t('Flagged for mulligan')}
    >
      <svg style={{ width: '0.75cqw', height: '0.75cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <path d="M4 4 L12 12 M12 4 L4 12" />
      </svg>
    </div>
  );
}

function HandRevealBadge() {
  return (
    <div
      className="absolute top-1 right-1 flex items-center justify-center rounded-full pointer-events-none"
      style={{
        width: 18,
        height: 18,
        background: 'rgba(0, 0, 0, 0.65)',
        border: `1px solid ${ACCENT_GOLD}`,
        boxShadow: `0 0 6px rgba(0,0,0,0.5), 0 0 4px ${ACCENT_GOLD}55`,
        color: ACCENT_GOLD,
      }}
      title={t('Revealed to opponent')}
    >
      <svg style={{ width: '0.6875cqw', height: '0.6875cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1.5 8s2.5-5 6.5-5 6.5 5 6.5 5-2.5 5-6.5 5-6.5-5-6.5-5Z" />
        <circle cx="8" cy="8" r="2" />
      </svg>
    </div>
  );
}

// Separation marker for an "Only It Can Cast" spell sitting in hand
// (Char Omphalos, Morgana le Fay, Archangel Gabriel). The violet lock
// keeps the owner-locked draw visually distinct from the rest of the
// hand so it isn't mistaken for a normally-castable spell.
const CAST_LOCK_VIOLET = '#b28ce6';
function HandCastLockBadge() {
  return (
    <div
      className="absolute top-1 left-1 flex items-center justify-center rounded-full pointer-events-none"
      style={{
        width: 18,
        height: 18,
        background: 'rgba(0, 0, 0, 0.65)',
        border: `1px solid ${CAST_LOCK_VIOLET}`,
        boxShadow: `0 0 6px rgba(0,0,0,0.5), 0 0 4px ${CAST_LOCK_VIOLET}55`,
        color: CAST_LOCK_VIOLET,
      }}
      title={t('Only the card that drew it can cast this spell')}
    >
      <svg style={{ width: '0.6875cqw', height: '0.6875cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3.5" y="7" width="9" height="6.5" rx="1" />
        <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
      </svg>
    </div>
  );
}

// Small clickable eye badge overlaid on the Collection round button.
// Clicking it toggles whether the local Collection is publicly visible
// to the opponent (a tab appears in their pile-search dialog while on).
// `stopPropagation` prevents the parent Collection button from opening
// the pile-search dialog when the badge is clicked.
function CollectionVisibilityBadge({ isPublic, onToggle }) {
  const accent = isPublic ? ACCENT_GOLD : `${GOLD} 0.55)`;
  return (
    <button
      type="button"
      aria-label={isPublic ? t('Hide Collection from opponent') : t('Show Collection to opponent')}
      title={isPublic ? t('Collection is visible to opponent (click to hide)') : t('Collection is private (click to show to opponent)')}
      className="absolute -top-1 -right-1 flex items-center justify-center rounded-full cursor-pointer transition-all"
      style={{
        width: '1.75cqw',
        height: '1.75cqw',
        background: isPublic ? 'rgba(74, 56, 22, 0.95)' : 'rgba(12, 10, 8, 0.92)',
        border: `1.5px solid ${accent}`,
        boxShadow: isPublic
          ? `0 0 10px ${ACCENT_GOLD}80, 0 2px 4px rgba(0,0,0,0.55)`
          : '0 2px 4px rgba(0,0,0,0.55)',
        color: isPublic ? ACCENT_GOLD : TEXT_MUTED,
      }}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
    >
      {isPublic ? (
        <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1.5 8s2.5-5 6.5-5 6.5 5 6.5 5-2.5 5-6.5 5-6.5-5-6.5-5Z" />
          <circle cx="8" cy="8" r="2" fill="currentColor" />
        </svg>
      ) : (
        <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 5.5c2 3 4 4.5 6 4.5s4-1.5 6-4.5" />
          <path d="M3 9.5 L2 11.5" />
          <path d="M13 9.5 L14 11.5" />
          <path d="M6 10.2 L5.4 12.2" />
          <path d="M10 10.2 L10.6 12.2" />
        </svg>
      )}
    </button>
  );
}
